// IDPMonitoringDashboard.jsx - Part 1: Imports, State, and Handler Functions

import React, { useEffect, useState, useContext, Fragment } from "react";
import axios from "axios";
import { AuthContext } from "../context/AuthContext";
import { toast } from "sonner";
import { PieChart, Pie, Cell, Legend, Tooltip, ResponsiveContainer } from "recharts";
import {
    hasOrganizationAccess,
    canHaveTeamAccess,
    getIDPAccessLevel
} from "../config/rolePermissions";


const IDPMonitoringDashboard = () => {


    const { user } = useContext(AuthContext);

    // Core state
    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(true);

    // Percentage editing state
    const [editingPercentage, setEditingPercentage] = useState(null);
    const [savingPercentage, setSavingPercentage] = useState(false);

    // Add Training Modal state
    const [showAddTrainingModal, setShowAddTrainingModal] = useState(false);
    const [selectedTeamMembers, setSelectedTeamMembers] = useState([]);
    const [addingTraining, setAddingTraining] = useState(false);

    const [searchFilter, setSearchFilter] = useState("");
    const [filterType, setFilterType] = useState("all"); // "

    // Add these state variables after your existing state declarations
    const [profileImage, setProfileImage] = useState(null);
    const [profileImagePreview, setProfileImagePreview] = useState(null);
    const [uploadingProfileImage, setUploadingProfileImage] = useState(false);

    const [hasTeamAccess, setHasTeamAccess] = useState(false);
    const [checkingTeamAccess, setCheckingTeamAccess] = useState(false);
    const [teamAccessChecked, setTeamAccessChecked] = useState(false);



    // STEP 1: Add additional modal state variables
    const [modalSearchFilter, setModalSearchFilter] = useState("");
    const [modalFilterType, setModalFilterType] = useState("all"); // "all", "name", "department", "team"
    const [modalSelectionMode, setModalSelectionMode] = useState("individual"); // "individual", "team", "department"
    const [expandedModalTeams, setExpandedModalTeams] = useState(new Set());


    const [trainingForm, setTrainingForm] = useState({
        activity: "",
        expectedOutcome: "",
        status: "Scheduled",
        priority: "Medium",
        type: "Internal",
        amount: "",
        lockin: "",
        percentage: "0%",
        validity: "",
        startdate: "",
        enddate: "",
        links: "",
        eventCategory: "Leadership Training"
    });

    // Accordion state for monitoring view
    const [expandedRows, setExpandedRows] = useState(new Set());
    // Completion proof state
    const [showCompletionModal, setShowCompletionModal] = useState(false);
    const [completionData, setCompletionData] = useState(null);
    const [completionImage, setCompletionImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [uploadingProof, setUploadingProof] = useState(false);


    // Image viewing state
    const [selectedProofImage, setSelectedProofImage] = useState(null);
    const [showProofModal, setShowProofModal] = useState(false);



    // View management
    // Replace your existing getCanSwitchViews function with this:
    const getCanSwitchViews = () => {
        // Organization access roles can always switch views
        if (hasOrganizationAccess(user?.role)) {
            return true;
        }

        // Team access roles can switch if they have team members
        if (canHaveTeamAccess(user?.role)) {
            return hasTeamAccess;
        }

        // Dynamic access for unknown roles
        if (getIDPAccessLevel(user?.role) === "dynamic") {
            return hasTeamAccess;
        }

        // Personal only roles cannot switch
        return false;
    };


    const canSwitchViews = getCanSwitchViews();
    // FIXED: Initialize view mode after team access is determined
    const [viewMode, setViewMode] = useState("self"); // Start with self by default
    const handleDownloadReport = async () => {
        if (!["hr", "excom", "manager", "supervisor"].includes(user?.role)) {
            toast.error("Access denied – Only HR, EXCOM, Manager, or Supervisor can download reports");
            return;
        }

        setDownloadingReport(true);

        try {
            console.log("🔄 Starting IDP report download...");

            const headers = {
                "x-username": user.username,
                "Content-Type": "application/json"
            };

            const response = await axios.get(
                "http://localhost:5000/api/idp/export-xlsx",
                {
                    headers,
                    responseType: 'blob'
                }
            );

            const blob = new Blob([response.data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;

            const currentDate = new Date().toISOString().split('T')[0];
            link.download = `IDP_Report_${currentDate}.xlsx`;

            document.body.appendChild(link);
            link.click();

            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            toast.success("Report downloaded successfully! 📊");

        } catch (error) {
            console.error("❌ Report download error:", error);
            if (error.response?.status === 403) {
                toast.error("Access denied – You don't have permission to download reports");
            } else {
                toast.error("Failed to download report. Please try again.");
            }
        } finally {
            setDownloadingReport(false);
        }
    };

    // Add these profile image handling functions
    // Add this state variable for storing the fetched profile image
    const [userProfileImage, setUserProfileImage] = useState(null);

    // Function to fetch user's profile image
    const fetchUserProfileImage = async () => {
        try {
            console.log("🔄 Fetching user profile image...");

            const response = await axios.get(
                `http://localhost:5000/api/idp/profile-image/${user.username}`,
                {
                    headers: { "x-username": user.username }
                }
            );

            if (response.data.success) {
                setUserProfileImage(response.data.data);
                console.log("✅ Profile image fetched successfully");
            }
        } catch (error) {
            console.log("ℹ️ No profile image found or failed to fetch:", error.message);
            setUserProfileImage(null);
        }
    };

    // Load profile image when component mounts or user changes
    useEffect(() => {
        if (user?.username) {
            fetchUserProfileImage();
        }
    }, [user?.username]);

    const handleProfileImageSelect = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        try {
            console.log(`📂 Selected file: ${file.name} (${(file.size / 1024 / 1024).toFixed(2)}MB)`);

            // Validate file type
            const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
            if (!allowedTypes.includes(file.type)) {
                toast.error("Please select a valid image file (JPEG, PNG, WEBP)");
                event.target.value = '';
                return;
            }

            // Check file size
            const maxSize = 5 * 1024 * 1024; // 5MB limit
            if (file.size > maxSize) {
                toast.error("Image file size must be less than 5MB");
                event.target.value = '';
                return;
            }

            setUploadingProfileImage(true);
            toast.info("Processing profile image...");

            // Compress the image using existing function
            const compressedFile = await compressImage(file, 512, 0.8);

            const compressedFileObj = new File([compressedFile], file.name, {
                type: 'image/jpeg',
                lastModified: Date.now()
            });

            console.log(`✅ Compression complete: ${(file.size / 1024 / 1024).toFixed(2)}MB → ${(compressedFileObj.size / 1024 / 1024).toFixed(2)}MB`);

            setProfileImage(compressedFileObj);

            // Create preview
            const reader = new FileReader();
            reader.onload = (e) => {
                setProfileImagePreview(e.target.result);
                setUploadingProfileImage(false);
                toast.success(`Image ready! Compressed to ${(compressedFileObj.size / 1024 / 1024).toFixed(2)}MB`);
            };

            reader.onerror = () => {
                toast.error("Failed to create image preview");
                setUploadingProfileImage(false);
                event.target.value = '';
            };

            reader.readAsDataURL(compressedFileObj);

        } catch (error) {
            console.error('❌ Profile image processing error:', error);
            toast.error(`Failed to process image: ${error.message}`);
            setUploadingProfileImage(false);
            event.target.value = '';
            setProfileImage(null);
            setProfileImagePreview(null);
        }
    };

    const handleProfileImageUpload = async () => {
        if (!profileImage) {
            toast.error("Please select an image first");
            return;
        }

        if (!user?.username) {
            toast.error("User session invalid. Please refresh and try again.");
            return;
        }

        setUploadingProfileImage(true);

        try {
            console.log("🔄 Starting profile image upload process...");
            console.log(`📊 File details: ${profileImage.name} (${(profileImage.size / 1024).toFixed(2)}KB)`);

            // Convert to base64 using existing function
            toast.info("Converting image...", { duration: 1000 });
            const imageBase64 = await convertImageToBase64(profileImage);

            if (!imageBase64 || !imageBase64.startsWith('data:image/')) {
                throw new Error("Invalid image conversion result");
            }

            const base64SizeMB = (imageBase64.length / 1024 / 1024).toFixed(2);
            console.log(`✅ Base64 conversion complete. Size: ${base64SizeMB}MB`);

            const headers = {
                "x-username": user.username,
                "Content-Type": "application/json"
            };

            const requestData = {
                image: imageBase64,
                fileName: profileImage.name,
                fileSize: profileImage.size,
                fileType: profileImage.type,
                uploadDate: new Date().toISOString()
            };

            console.log("📤 Uploading to server...");
            toast.info("Uploading to server...", { duration: 2000 });

            const response = await axios.put(
                "http://localhost:5000/api/idp/upload-profile-image-alt",
                requestData,
                {
                    headers,
                    timeout: 120000, // 2 minutes
                    maxContentLength: 50 * 1024 * 1024,
                    maxBodyLength: 50 * 1024 * 1024,
                    onUploadProgress: (progressEvent) => {
                        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                        console.log(`📊 Upload progress: ${progress}%`);
                    }
                }
            );

            console.log("✅ Server response:", response.data);

            if (response.data.success) {
                toast.success("Profile image uploaded successfully! 🎉");

                // Clear upload states
                setProfileImage(null);
                setProfileImagePreview(null);

                // Clear file input
                const fileInput = document.getElementById('header-profile-upload');
                if (fileInput) fileInput.value = '';

                // 🔧 FETCH THE UPDATED PROFILE IMAGE
                console.log("🔄 Fetching updated profile image...");
                toast.info("Refreshing profile image...", { duration: 1000 });

                // Wait a moment for the database to update, then fetch
                setTimeout(async () => {
                    await fetchUserProfileImage();
                    toast.success("Profile updated! 🖼️");
                }, 500);

            } else {
                throw new Error(response.data.message || 'Upload failed with unknown error');
            }

        } catch (error) {
            console.error("❌ Profile image upload error:", error);

            let errorMessage = "Upload failed. Please try again.";

            if (error.response) {
                const status = error.response.status;
                const serverMessage = error.response.data?.message;

                console.error("❌ Server error details:", {
                    status,
                    data: error.response.data,
                    headers: error.response.headers
                });

                switch (status) {
                    case 400:
                        errorMessage = serverMessage || "Invalid image data. Please try a different image.";
                        break;
                    case 401:
                        errorMessage = "Authentication failed. Please refresh and try again.";
                        break;
                    case 404:
                        errorMessage = serverMessage || "User not found. Please contact support.";
                        break;
                    case 413:
                        errorMessage = "Image too large. Please use a smaller image.";
                        break;
                    case 500:
                        errorMessage = serverMessage || "Server error. Please try again later.";
                        break;
                    default:
                        errorMessage = serverMessage || `Upload failed (Error ${status})`;
                }
            } else if (error.request) {
                errorMessage = "Network error. Please check your connection and try again.";
            } else if (error.code === 'ECONNABORTED') {
                errorMessage = "Upload timeout. Please try with a smaller image.";
            } else {
                errorMessage = error.message || "An unexpected error occurred.";
            }

            toast.error(errorMessage);

        } finally {
            setUploadingProfileImage(false);
        }
    };
    // =============================================================================
    // DATA FETCHING
    // =============================================================================

    const checkTeamAccess = async () => {
        if (!user?.username) {
            setHasTeamAccess(false);
            setTeamAccessChecked(true);
            return false;
        }

        setCheckingTeamAccess(true);
        try {
            const headers = {
                "x-username": user.username,
                "Content-Type": "application/json"
            };
            const accessLevel = getIDPAccessLevel(user.role);
            console.log(`🔍 Checking IDP access for ${user.role} ${user.name} (Level: ${accessLevel})`);

            // Organization access roles don't need team check
            if (hasOrganizationAccess(user.role)) {
                console.log(`✅ ${user.name} has organization-wide IDP access`);
                setHasTeamAccess(true);
                setTeamAccessChecked(true);
                return true;
            }

            // Team access roles - check if they actually have team members
            if (canHaveTeamAccess(user.role) || accessLevel === "dynamic") {
                try {
                    const response = await axios.get("http://localhost:5000/api/idp/team", { headers });

                    const hasTeam = response.data.success &&
                        response.data.data &&
                        response.data.data.length > 0;

                    setHasTeamAccess(hasTeam);
                    setTeamAccessChecked(true);

                    if (hasTeam) {
                        console.log(`✅ ${user.name} (${user.role}) has team IDP access - ${response.data.data.length} members`);
                    } else {
                        console.log(`ℹ️ ${user.name} (${user.role}) has no team members for IDP access`);
                    }

                    return hasTeam;
                } catch (teamError) {
                    // FIXED: Handle 403 errors gracefully
                    if (teamError.response?.status === 403) {
                        console.log(`🔒 ${user.name} (${user.role}) denied team access - likely no permission or no team`);
                        setHasTeamAccess(false);
                        setTeamAccessChecked(true);
                        return false;
                    }
                    throw teamError; // Re-throw other errors
                }
            }

            // Personal only roles
            console.log(`ℹ️ ${user.name} (${user.role}) has personal IDP access only`);
            setHasTeamAccess(false);
            setTeamAccessChecked(true);
            return false;

        } catch (error) {
            console.error("❌ Error checking IDP team access:", error);
            setHasTeamAccess(false);
            setTeamAccessChecked(true);
            return false;
        } finally {
            setCheckingTeamAccess(false);
        }
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            let url = "http://localhost:5000/api/idp/self";
            let dataDescription = "personal IDP";

            if (viewMode === "monitoring" && canSwitchViews) {
                // Organization access roles (Senior + Decision makers) get full access
                if (hasOrganizationAccess(user.role)) {
                    url = "http://localhost:5000/api/idp/all";
                    dataDescription = "organization-wide IDPs";
                    console.log(`📊 ${user.role} accessing ALL organization IDP data`);
                }
                // Team access roles (Junior level) get team access only
                else if (canHaveTeamAccess(user.role) || getIDPAccessLevel(user.role) === "dynamic") {
                    // Ensure team access is checked before making request
                    if (!teamAccessChecked) {
                        console.log("🔄 Team access not checked yet, checking now...");
                        const hasTeam = await checkTeamAccess();
                        if (!hasTeam) {
                            console.log(`ℹ️ ${user.name} (${user.role}) has no team, switching to personal view`);
                            toast.info(`No team members found. Showing your personal IDP.`);
                            setViewMode("self");
                            url = "http://localhost:5000/api/idp/self";
                            dataDescription = "personal IDP";
                        } else {
                            url = "http://localhost:5000/api/idp/team";
                            dataDescription = "team IDPs";
                            console.log(`👥 ${user.role} accessing TEAM IDP data only`);
                        }
                    } else {
                        if (hasTeamAccess) {
                            url = "http://localhost:5000/api/idp/team";
                            dataDescription = "team IDPs";
                            console.log(`👥 ${user.role} accessing TEAM IDP data only`);
                        } else {
                            console.log(`ℹ️ ${user.name} (${user.role}) has no team, showing personal IDP`);
                            toast.info(`No team members found. Showing your personal IDP.`);
                            url = "http://localhost:5000/api/idp/self";
                            dataDescription = "personal IDP";
                            setViewMode("self");
                        }
                    }
                } else {
                    // For roles that don't have monitoring access, prevent the request and stay on self
                    console.log(`ℹ️ ${user.name} (${user.role}) has personal access only, staying on self view`);
                    toast.info(`You have access to your personal IDP only.`);
                    setViewMode("self");
                    url = "http://localhost:5000/api/idp/self";
                    dataDescription = "personal IDP";
                }
            }

            if (!user?.username) {
                toast.error("Username is missing - please log in again");
                return;
            }

            const headers = {
                "x-username": user.username,
                "Content-Type": "application/json"
            };

            console.log(`📡 Fetching ${dataDescription} from: ${url}`);
            const response = await axios.get(url, { headers });

            if (response.data.success) {
                const data = response.data.data || [];
                setRecords(data);

                console.log(`✅ Loaded ${data.length} records for ${dataDescription}`);
            } else {
                console.warn(`⚠️ Server returned success: false for ${dataDescription}`);
                toast.error(response.data.message || "Failed to load IDP data");
                setRecords([]);
            }
        } catch (err) {
            console.error("❌ Fetch IDP data error:", err);

            // Enhanced error handling
            if (err.response?.status === 403) {
                console.log("🔒 Access denied, switching to personal view");
                toast.error("Access denied - switching to your personal IDP view");
                setViewMode("self");

                // Retry with self endpoint as fallback
                try {
                    console.log("🔄 Attempting fallback to personal IDP...");
                    const headers = {
                        "x-username": user.username,
                        "Content-Type": "application/json"
                    };
                    const selfResponse = await axios.get("http://localhost:5000/api/idp/self", { headers });

                    if (selfResponse.data.success) {
                        setRecords(selfResponse.data.data || []);
                        console.log(`✅ Fallback successful: Loaded ${selfResponse.data.data?.length || 0} personal records`);
                        toast.success("Personal IDP loaded successfully");
                    } else {
                        throw new Error(selfResponse.data.message || "Self endpoint returned success: false");
                    }
                } catch (selfErr) {
                    console.error("❌ Fallback to self endpoint also failed:", selfErr);
                    toast.error("Failed to load any IDP data - please contact support");
                    setRecords([]);
                }
            } else if (err.response?.status === 401) {
                console.error("🔐 Authentication failed");
                toast.error("Session expired - please log in again");
                setRecords([]);
            } else if (err.response?.status === 500) {
                console.error("🔥 Server error");
                toast.error("Server error - please try again later");
                setRecords([]);
            } else if (err.code === 'ECONNABORTED') {
                console.error("⏱️ Request timeout");
                toast.error("Request timeout - please check your connection");
                setRecords([]);
            } else if (!err.response) {
                console.error("🌐 Network error");
                toast.error("Network error - please check your connection");
                setRecords([]);
            } else {
                // Generic error handling
                const errorMessage = err.response?.data?.message || "An unexpected error occurred";
                toast.error(errorMessage);
                setRecords([]);
            }
        } finally {
            setLoading(false);
        }
    };


    useEffect(() => {
        if (user) {
            // FIXED: Check team access first, then fetch data
            const initializeData = async () => {
                if (!teamAccessChecked && canHaveTeamAccess(user.role)) {
                    await checkTeamAccess();
                }
                await fetchData();
            };

            initializeData();
        }
    }, [user, viewMode, teamAccessChecked]); // Added teamAccessChecked as dependency


    // Add this useEffect to set the correct initial view mode
    useEffect(() => {
        if (user && teamAccessChecked) {
            const canSwitch = getCanSwitchViews();
            if (canSwitch && viewMode === "self") {
                // Only auto-switch if we started with "self" and now have access
                setViewMode("monitoring");
            } else if (!canSwitch && viewMode === "monitoring") {
                // Switch back to self if we lost access
                setViewMode("self");
            }
        }
    }, [user, teamAccessChecked, hasTeamAccess]);
    // =============================================================================
    // VIEW MODE MANAGEMENT
    // =============================================================================

    const handleViewModeChange = (newMode) => {
        if (!canSwitchViews && newMode !== "self") {
            toast.error("Access denied - You can only view your own IDP");
            return;
        }
        setViewMode(newMode);
    };





    // =============================================================================
    // ADD TRAINING MODAL FUNCTIONS
    // =============================================================================

    const handleOpenAddTrainingModal = () => {
        setShowAddTrainingModal(true);
        setSelectedTeamMembers([]);
        // ADD THESE 4 NEW LINES ⬇️
        setModalSearchFilter("");
        setModalFilterType("all");
        setModalSelectionMode("individual");
        setExpandedModalTeams(new Set());

        // Keep your existing form reset:
        setTrainingForm({
            activity: "",
            expectedOutcome: "",
            status: "Scheduled",
            priority: "Medium",
            type: "Internal",
            amount: "",
            lockin: "",
            percentage: "0%",
            validity: "",
            startdate: "",
            enddate: "",
            links: "",
            eventCategory: "Leadership Training"
        });
    };

    const handleSelectEntireTeam = (teamMembers, isSelected) => {
        const teamUserIds = teamMembers.map(member => member.userID);
        if (isSelected) {
            setSelectedTeamMembers(prev => [...new Set([...prev, ...teamUserIds])]);
        } else {
            setSelectedTeamMembers(prev => prev.filter(id => !teamUserIds.includes(id)));
        }
    };
    const handleSelectEntireDepartment = (deptMembers, isSelected) => {
        const deptUserIds = deptMembers.map(member => member.userID);
        if (isSelected) {
            setSelectedTeamMembers(prev => [...new Set([...prev, ...deptUserIds])]);
        } else {
            setSelectedTeamMembers(prev => prev.filter(id => !deptUserIds.includes(id)));
        }
    };


    const toggleModalTeamExpansion = (teamKey) => {
        const newExpanded = new Set(expandedModalTeams);
        if (newExpanded.has(teamKey)) {
            newExpanded.delete(teamKey);
        } else {
            newExpanded.add(teamKey);
        }
        setExpandedModalTeams(newExpanded);
    };

    const getFilteredModalMembers = () => {
        if (!modalSearchFilter.trim()) return records || [];

        const searchTerm = modalSearchFilter.toLowerCase().trim();
        return (records || []).filter(member => {
            switch (modalFilterType) {
                case "name":
                    return member.name?.toLowerCase().includes(searchTerm) ||
                        member.username?.toLowerCase().includes(searchTerm);
                case "department":
                    return member.department?.toLowerCase().includes(searchTerm);
                case "team":
                    return member.supervisor?.toLowerCase().includes(searchTerm);
                case "all":
                default:
                    return member.name?.toLowerCase().includes(searchTerm) ||
                        member.username?.toLowerCase().includes(searchTerm) ||
                        member.department?.toLowerCase().includes(searchTerm) ||
                        member.supervisor?.toLowerCase().includes(searchTerm);
            }
        });
    };


    const handleCloseAddTrainingModal = () => {
        setShowAddTrainingModal(false);
        setSelectedTeamMembers([]);
        // ADD THESE 4 NEW LINES ⬇️
        setModalSearchFilter("");
        setModalFilterType("all");
        setModalSelectionMode("individual");
        setExpandedModalTeams(new Set());
    };

    const handleTeamMemberSelect = (userID, isSelected) => {
        if (isSelected) {
            setSelectedTeamMembers(prev => [...prev, userID]);
        } else {
            setSelectedTeamMembers(prev => prev.filter(u => u !== userID));
        }
    };

    const handleSelectAllTeamMembers = (isSelected) => {
        if (isSelected) {
            setSelectedTeamMembers((records || []).map(record => record.userID));
        } else {
            setSelectedTeamMembers([]);
        }
    };

    const handleTrainingFormChange = (field, value) => {
        setTrainingForm(prev => ({ ...prev, [field]: value }));
    };

    const handleSubmitTraining = async () => {
        // Input validation
        if (!trainingForm.activity.trim()) {
            toast.error("Training activity name is required");
            return;
        }

        if (selectedTeamMembers.length === 0) {
            toast.error("Please select at least one team member");
            return;
        }

        // Debug logging
        console.log("🚀 Frontend sending data:");
        console.log("Selected team members:", selectedTeamMembers);
        console.log("Training form:", trainingForm);
        console.log("Request payload:", {
            targetUserIds: selectedTeamMembers,
            trainingData: trainingForm
        });

        setAddingTraining(true);

        try {
            const headers = {
                "x-username": user.username,
                "Content-Type": "application/json"
            };

            const response = await axios.post(
                "http://localhost:5000/api/idp/add-bulk-training",
                {
                    targetUserIds: selectedTeamMembers,
                    trainingData: trainingForm
                },
                { headers }
            );

            // Debug the response
            console.log("🔍 Backend response:", response.data);
            console.log("🔍 Success status:", response.data.success);
            console.log("🔍 Message:", response.data.message);

            // Handle successful response
            if (response.data.success) {
                console.log("✅ Training assignment successful");

                // Show success notification
                toast.success(response.data.message || "Training added successfully!");

                // Close the modal
                handleCloseAddTrainingModal();

                // Refresh the data to show the new training
                console.log("🔄 Refreshing team data...");
                await fetchData();

                // Show additional info if there were partial failures
                if (response.data.data?.summary) {
                    const { successful, created, failed, total } = response.data.data.summary;

                    if (failed > 0) {
                        // Show warning for partial failures
                        setTimeout(() => {
                            toast.warning(
                                `${successful + created} out of ${total} users processed successfully. ${failed} failed - check console for details.`
                            );
                        }, 1500);

                        // Log failed users for debugging
                        if (response.data.data.results?.failed?.length > 0) {
                            console.warn("❌ Failed users:", response.data.data.results.failed);
                        }
                    } else if (created > 0) {
                        // Show info about new IDP records created
                        setTimeout(() => {
                            toast.info(`Training added! ${created} new IDP record(s) were created.`);
                        }, 1500);
                    }
                }

            } else {
                // Backend returned success: false
                console.error("❌ Backend returned success: false");
                toast.error(response.data.message || "Failed to add training");
            }

        } catch (error) {
            console.error("❌ Training submission error:", error);
            console.error("❌ Error response:", error.response?.data);

            // Handle different types of errors
            if (error.response) {
                // Server responded with error status
                const status = error.response.status;
                const errorData = error.response.data;

                switch (status) {
                    case 400:
                        toast.error(errorData.message || "Invalid request - please check your input");
                        break;
                    case 401:
                        toast.error("Authentication failed - please log in again");
                        break;
                    case 403:
                        toast.error(errorData.message || "Access denied - you don't have permission to assign training to these users");
                        break;
                    case 404:
                        toast.error(errorData.message || "Selected users not found");
                        break;
                    case 500:
                        toast.error("Server error - please try again later");
                        break;
                    default:
                        toast.error(errorData.message || `Request failed with status ${status}`);
                }
            } else if (error.request) {
                // Network error
                console.error("❌ Network error:", error.request);
                toast.error("Network error - please check your connection and try again");
            } else {
                // Other error
                console.error("❌ Unexpected error:", error.message);
                toast.error("An unexpected error occurred - please try again");
            }

        } finally {
            setAddingTraining(false);
        }
    };



    // =============================================================================
    // ACCORDION FUNCTIONS
    // =============================================================================

    const toggleAccordionRow = (index) => {
        const newExpandedRows = new Set(expandedRows);
        if (newExpandedRows.has(index)) {
            newExpandedRows.delete(index);
        } else {
            newExpandedRows.add(index);
        }
        setExpandedRows(newExpandedRows);
    };

    // =============================================================================
    // PERCENTAGE EDITING FUNCTIONS
    // =============================================================================


    const handlePercentageEdit = (activityIndex, currentValue) => {
        setEditingPercentage({
            activityIndex,
            value: currentValue.replace('%', '')
        });
    };

    const handleMonitoringPercentageEdit = (recordIndex, activityIndex, currentValue, targetUsername) => {
        if (!["hr", "excom", "manager", "supervisor"].includes(user.role)) {
            toast.error("Access denied - You don't have permission to edit this data");
            return;
        }

        setEditingPercentage({
            recordIndex,
            activityIndex,
            value: currentValue.replace('%', ''),
            targetUsername
        });
    };

    const handlePercentageChange = (value) => {
        const numericValue = value.replace(/[^0-9]/g, '');
        if (numericValue === '') {
            setEditingPercentage(prev => ({ ...prev, value: '0' }));
            return;
        }

        const percentage = Math.min(100, Math.max(0, parseInt(numericValue) || 0));

        // Check if user is trying to input 100%
        if (percentage === 100) {
            // Check if already has completion proof
            let currentActivity;
            if (editingPercentage.targetUsername) {
                // Monitoring view
                currentActivity = records[editingPercentage.recordIndex]?.developmentalActivities?.[editingPercentage.activityIndex];
            } else {
                // Self view
                currentActivity = records[0]?.developmentalActivities?.[editingPercentage.activityIndex];
            }

            if (!currentActivity?.completionProof) {
                // Show completion modal
                setCompletionData({
                    recordIndex: editingPercentage.recordIndex,
                    activityIndex: editingPercentage.activityIndex,
                    targetUsername: editingPercentage.targetUsername
                });
                setShowCompletionModal(true);
                return;
            }
        }

        setEditingPercentage(prev => ({ ...prev, value: percentage.toString() }));
    };

    const handlePercentageSave = async () => {
        if (!editingPercentage || savingPercentage) return;

        setSavingPercentage(true);
        try {
            const { activityIndex, value } = editingPercentage;
            const percentageValue = `${value}%`;

            const headers = {
                "x-username": user.username,
                "Content-Type": "application/json"
            };

            const response = await axios.put(
                `http://localhost:5000/api/idp/update-activity/${activityIndex}`,
                { percentage: percentageValue },
                { headers }
            );

            if (response.data.success) {
                setRecords(prevRecords => {
                    const updatedRecords = [...prevRecords];
                    if (updatedRecords[0]?.developmentalActivities?.[activityIndex]) {
                        updatedRecords[0].developmentalActivities[activityIndex].percentage = percentageValue;
                    }
                    return updatedRecords;
                });
                toast.success(`Percentage updated to ${percentageValue}`);
                setEditingPercentage(null);
            } else {
                toast.error(response.data.message || "Failed to update percentage");
            }
        } catch (error) {
            toast.error(error.response?.data?.message || "Failed to update percentage");
        } finally {
            setSavingPercentage(false);
        }
    };



    const handleMonitoringPercentageSave = async () => {
        if (!editingPercentage || savingPercentage) return;

        setSavingPercentage(true);
        try {
            const { recordIndex, activityIndex, value, targetUsername } = editingPercentage;
            const percentageValue = `${value}%`;

            const headers = {
                "x-username": targetUsername,
                "Content-Type": "application/json"
            };

            const response = await axios.put(
                `http://localhost:5000/api/idp/update-activity/${activityIndex}`,
                { percentage: percentageValue },
                { headers }
            );

            if (response.data.success) {
                setRecords(prevRecords => {
                    const updatedRecords = [...prevRecords];
                    if (updatedRecords[recordIndex]?.developmentalActivities?.[activityIndex]) {
                        updatedRecords[recordIndex].developmentalActivities[activityIndex].percentage = percentageValue;
                    }
                    return updatedRecords;
                });
                toast.success(`Percentage updated to ${percentageValue} for ${(records || [])[recordIndex]?.name}`);
                setEditingPercentage(null);
            } else {
                toast.error(response.data.message || "Failed to update percentage");
            }
        } catch (error) {
            toast.error(error.response?.data?.message || "Failed to update percentage");
        } finally {
            setSavingPercentage(false);
        }
    };

    const handlePercentageCancel = () => {
        setEditingPercentage(null);
    };

    const handlePercentageKeyPress = (e) => {
        if (e.key === 'Enter') {
            if (editingPercentage?.targetUsername) {
                handleMonitoringPercentageSave();
            } else {
                handlePercentageSave();
            }
        } else if (e.key === 'Escape') {
            handlePercentageCancel();
        }
    };

    // =============================================================================
    // UTILITY FUNCTIONS
    // =============================================================================

    const getProgressColor = (percentage) => {
        if (percentage >= 80) return 'text-green-600 dark:text-green-400';
        if (percentage >= 60) return 'text-yellow-600 dark:text-yellow-400';
        if (percentage >= 40) return 'text-yellow-600 dark:text-yellow-400';
        if (percentage >= 20) return 'text-orange-600 dark:text-orange-400';
        return 'text-red-600 dark:text-red-400';
    };

    const getProgressMessage = (percentage) => {
        if (percentage >= 80) return 'Excellent Progress! 🎉';
        if (percentage >= 60) return 'Good Progress! 👍';
        if (percentage >= 40) return 'Making Progress 📈';
        if (percentage >= 20) return 'Getting Started 🚀';
        return 'Just Beginning 📚';
    };

    const getProgressBgColor = (percentage) => {
        if (percentage >= 80) return 'bg-green-500';
        if (percentage >= 60) return 'bg-yellow-500';
        if (percentage >= 40) return 'bg-yellow-500';
        if (percentage >= 20) return 'bg-orange-500';
        return 'bg-red-500';
    };

    const getModalTeamGroups = () => {
        const teamGroups = (records || []).reduce((acc, record) => {
            const supervisor = record.supervisor || 'No Supervisor Assigned';
            if (!acc[supervisor]) {
                acc[supervisor] = [];
            }
            acc[supervisor].push(record);
            return acc;
        }, {});

        if (!modalSearchFilter.trim()) {
            return teamGroups;
        }

        const searchTerm = modalSearchFilter.toLowerCase().trim();
        const filteredGroups = {};

        Object.entries(teamGroups).forEach(([supervisor, members]) => {
            const supervisorMatches = supervisor.toLowerCase().includes(searchTerm);

            const filteredMembers = members.filter(member => {
                switch (modalFilterType) {
                    case "name":
                        return member.name?.toLowerCase().includes(searchTerm) ||
                            member.username?.toLowerCase().includes(searchTerm);
                    case "department":
                        return member.department?.toLowerCase().includes(searchTerm);
                    case "team":
                        return supervisor.toLowerCase().includes(searchTerm);
                    case "all":
                    default:
                        return member.name?.toLowerCase().includes(searchTerm) ||
                            member.username?.toLowerCase().includes(searchTerm) ||
                            member.department?.toLowerCase().includes(searchTerm) ||
                            member.position?.toLowerCase().includes(searchTerm);
                }
            });

            if (supervisorMatches || filteredMembers.length > 0) {
                filteredGroups[supervisor] = supervisorMatches ? members : filteredMembers;
            }
        });

        return filteredGroups;
    };

    const getDepartmentGroups = () => {
        const deptGroups = (records || []).reduce((acc, record) => {
            const department = record.department || 'No Department';
            if (!acc[department]) {
                acc[department] = [];
            }
            acc[department].push(record);
            return acc;
        }, {});

        if (!modalSearchFilter.trim()) {
            return deptGroups;
        }

        const searchTerm = modalSearchFilter.toLowerCase().trim();
        const filteredGroups = {};

        Object.entries(deptGroups).forEach(([department, members]) => {
            const deptMatches = department.toLowerCase().includes(searchTerm);

            const filteredMembers = members.filter(member => {
                return member.name?.toLowerCase().includes(searchTerm) ||
                    member.username?.toLowerCase().includes(searchTerm) ||
                    member.department?.toLowerCase().includes(searchTerm);
            });

            if (deptMatches || filteredMembers.length > 0) {
                filteredGroups[department] = deptMatches ? members : filteredMembers;
            }
        });

        return filteredGroups;
    };

    // ADD THIS ENTIRE FUNCTION HERE ⬇️
    const getFilteredTeamGroups = () => {
        // Group records by supervisor (team)
        const teamGroups = (records || []).reduce((acc, record) => {
            const supervisor = record.supervisor || 'No Supervisor Assigned';
            if (!acc[supervisor]) {
                acc[supervisor] = [];
            }
            acc[supervisor].push(record);
            return acc;
        }, {});

        if (!searchFilter.trim()) {
            return teamGroups;
        }

        const searchTerm = searchFilter.toLowerCase().trim();
        const filteredGroups = {};

        Object.entries(teamGroups).forEach(([supervisor, members]) => {
            const supervisorMatches = supervisor.toLowerCase().includes(searchTerm);

            // Filter members based on search term
            const filteredMembers = members.filter(member => {
                const memberMatches =
                    member.name?.toLowerCase().includes(searchTerm) ||
                    member.username?.toLowerCase().includes(searchTerm) ||
                    member.department?.toLowerCase().includes(searchTerm);

                return memberMatches;
            });

            // Include team if:
            // 1. filterType is "all" and (supervisor name matches OR has matching members)
            // 2. filterType is "teams" and supervisor name matches
            // 3. filterType is "members" and has matching members
            if (filterType === "all" && (supervisorMatches || filteredMembers.length > 0)) {
                filteredGroups[supervisor] = supervisorMatches ? members : filteredMembers;
            } else if (filterType === "teams" && supervisorMatches) {
                filteredGroups[supervisor] = members;
            } else if (filterType === "members" && filteredMembers.length > 0) {
                filteredGroups[supervisor] = filteredMembers;
            }
        });

        return filteredGroups;
    };

    // Date parsing helper function for DD-MM-YYYY format
    const parseDate = (dateString) => {
        if (!dateString) return null;

        // Handle DD-MM-YYYY format
        const parts = dateString.split('-');
        if (parts.length === 3) {
            const day = parseInt(parts[0], 10);
            const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
            const year = parseInt(parts[2], 10);

            if (!isNaN(day) && !isNaN(month) && !isNaN(year)) {
                return new Date(year, month, day);
            }
        }

        // Fallback to native Date parsing
        const fallbackDate = new Date(dateString);
        return isNaN(fallbackDate.getTime()) ? null : fallbackDate;
    };

    // IDPMonitoringDashboard.jsx - Part 2: Render Functions and Main Component Return
    // This continues from Part 1...

    // =============================================================================
    // COMPLETION PROOF FUNCTIONS
    // =============================================================================

    // =============================================================================
    // FIXED IMAGE HANDLING FUNCTIONS
    // =============================================================================

    // Image compression utility
    const compressImage = (file, maxWidth = 1024, quality = 0.8) => {
        return new Promise((resolve) => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            const img = new Image();

            img.onload = () => {
                // Calculate new dimensions
                let { width, height } = img;
                if (width > height) {
                    if (width > maxWidth) {
                        height = (height * maxWidth) / width;
                        width = maxWidth;
                    }
                } else {
                    if (height > maxWidth) {
                        width = (width * maxWidth) / height;
                        height = maxWidth;
                    }
                }

                // Set canvas size and draw image
                canvas.width = width;
                canvas.height = height;
                ctx.drawImage(img, 0, 0, width, height);

                // Convert to blob
                canvas.toBlob(resolve, 'image/jpeg', quality);
            };

            img.src = URL.createObjectURL(file);
        });
    };

    // Fixed image selection handler
    const handleImageSelect = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        try {
            // Validate file type
            const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
            if (!allowedTypes.includes(file.type)) {
                toast.error("Please select a valid image file (JPEG, PNG, WEBP)");
                return;
            }

            // Check original file size (before compression)
            const maxSize = 10 * 1024 * 1024; // 10MB original file limit
            if (file.size > maxSize) {
                toast.error("Image file size must be less than 10MB");
                return;
            }

            // Show loading state
            setUploadingProof(true);
            toast.info("Processing image...");

            // Compress the image
            const compressedFile = await compressImage(file, 1024, 0.8);

            // Check compressed file size
            const maxCompressedSize = 2 * 1024 * 1024; // 2MB after compression
            if (compressedFile.size > maxCompressedSize) {
                toast.error("Image is too large even after compression. Please use a smaller image.");
                setUploadingProof(false);
                return;
            }

            // Create File object from compressed blob
            const compressedFileObj = new File([compressedFile], file.name, {
                type: 'image/jpeg',
                lastModified: Date.now()
            });

            setCompletionImage(compressedFileObj);

            // Create preview
            const reader = new FileReader();
            reader.onload = (e) => {
                setImagePreview(e.target.result);
                setUploadingProof(false);
                toast.success(`Image compressed: ${(file.size / 1024 / 1024).toFixed(2)}MB → ${(compressedFileObj.size / 1024 / 1024).toFixed(2)}MB`);
            };
            reader.onerror = () => {
                toast.error("Failed to process image");
                setUploadingProof(false);
            };
            reader.readAsDataURL(compressedFileObj);

        } catch (error) {
            console.error('Image processing error:', error);
            toast.error("Failed to process image");
            setUploadingProof(false);
        }
    };

    // Enhanced base64 conversion with error handling
    const convertImageToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();

            reader.onload = () => {
                try {
                    const result = reader.result;

                    // Validate base64 result
                    if (!result || !result.startsWith('data:image/')) {
                        throw new Error('Invalid image data');
                    }

                    // Check final base64 size
                    const base64Size = result.length * 0.75; // Approximate base64 to bytes
                    const maxBase64Size = 3 * 1024 * 1024; // 3MB base64 limit

                    if (base64Size > maxBase64Size) {
                        throw new Error('Processed image is still too large');
                    }

                    console.log(`✅ Base64 conversion successful. Size: ${(base64Size / 1024 / 1024).toFixed(2)}MB`);
                    resolve(result);
                } catch (error) {
                    reject(error);
                }
            };

            reader.onerror = () => {
                reject(new Error('Failed to read image file'));
            };

            reader.readAsDataURL(file);
        });
    };

    const [downloadingReport, setDownloadingReport] = useState(false);


    // Enhanced completion submission with better error handling
    const handleCompletionSubmit = async () => {
        if (!completionImage) {
            toast.error("Please attach a completion proof image");
            return;
        }

        setUploadingProof(true);

        try {
            // Convert to base64 with validation
            const imageBase64 = await convertImageToBase64(completionImage);

            const headers = {
                "x-username": completionData.targetUsername || user.username,
                "Content-Type": "application/json"
            };

            const requestData = {
                percentage: "100%",
                completionProof: {
                    image: imageBase64,
                    fileName: completionImage.name,
                    fileSize: completionImage.size,
                    fileType: completionImage.type,
                    uploadDate: new Date().toISOString(),
                    originalSize: completionImage.size,
                    compressedSize: completionImage.size
                },
                status: "Attended"
            };

            // Log payload size for debugging
            const payloadSize = JSON.stringify(requestData).length;
            console.log(`📊 Upload payload size: ${(payloadSize / 1024 / 1024).toFixed(2)}MB`);

            if (payloadSize > 45 * 1024 * 1024) { // 45MB limit (leaving 5MB buffer)
                throw new Error("Payload too large. Please use a smaller image.");
            }

            const response = await axios.put(
                `http://localhost:5000/api/idp/update-activity/${completionData.activityIndex}`,
                requestData,
                {
                    headers,
                    timeout: 60000, // 60 second timeout for large uploads
                    maxContentLength: 50 * 1024 * 1024, // 50MB max content
                    maxBodyLength: 50 * 1024 * 1024 // 50MB max body
                }
            );

            if (response.data.success) {
                // Update local state based on whether it's monitoring or self view
                if (completionData.targetUsername) {
                    setRecords(prevRecords => {
                        const updatedRecords = [...prevRecords];
                        const activity = updatedRecords[completionData.recordIndex].developmentalActivities[completionData.activityIndex];
                        activity.percentage = "100%";
                        activity.completionProof = requestData.completionProof;
                        activity.status = "Attended";
                        return updatedRecords;
                    });
                    toast.success(`Training completed with proof for ${records[completionData.recordIndex]?.name}! 🎉`);
                } else {
                    setRecords(prevRecords => {
                        const updatedRecords = [...prevRecords];
                        const activity = updatedRecords[0].developmentalActivities[completionData.activityIndex];
                        activity.percentage = "100%";
                        activity.completionProof = requestData.completionProof;
                        activity.status = "Attended";
                        return updatedRecords;
                    });
                    toast.success("Training completed with proof! 🎉");
                }

                // Close modal and reset state
                setShowCompletionModal(false);
                setCompletionData(null);
                setCompletionImage(null);
                setImagePreview(null);
                setEditingPercentage(null);
            } else {
                throw new Error(response.data.message || 'Upload failed');
            }
        } catch (error) {
            console.error("❌ Completion proof upload error:", error);

            if (error.response) {
                const status = error.response.status;
                const errorData = error.response.data;

                switch (status) {
                    case 413:
                        toast.error("Image too large. Please use a smaller image or reduce quality.");
                        break;
                    case 400:
                        toast.error(errorData.message || "Invalid image data. Please try a different image.");
                        break;
                    case 401:
                        toast.error("Authentication failed. Please log in again.");
                        break;
                    case 403:
                        toast.error("Permission denied. You cannot edit this training.");
                        break;
                    case 404:
                        toast.error("Training activity not found.");
                        break;
                    case 500:
                        toast.error("Server error. Please try again later.");
                        break;
                    default:
                        toast.error(errorData.message || `Upload failed with status ${status}`);
                }
            } else if (error.code === 'ECONNABORTED') {
                toast.error("Upload timeout. Please try with a smaller image.");
            } else if (error.message.includes('too large')) {
                toast.error("Image too large after processing. Please use a smaller image.");
            } else {
                toast.error(error.message || "Failed to submit completion proof");
            }
        } finally {
            setUploadingProof(false);
        }
    };

    const renderEnhancedTeamSelection = () => {
        const teamGroups = getModalTeamGroups();
        const deptGroups = getDepartmentGroups();
        const allMembers = records || [];
        const filteredMembers = getFilteredModalMembers();

        return (
            <div className="mb-6">
                <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">Select Team Members</h3>

                {/* Enhanced Search and Selection Mode */}
                <div className="mb-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border">
                    {/* Search Bar */}
                    <div className="flex flex-col lg:flex-row gap-3 mb-4">
                        <div className="flex-1">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                🔍 Search Members
                            </label>
                            <div className="relative">
                                <input
                                    type="text"
                                    value={modalSearchFilter}
                                    onChange={(e) => setModalSearchFilter(e.target.value)}
                                    placeholder="Search by name, username, department, or team..."
                                    className="w-full px-3 py-2 pl-9 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                />
                                <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400">🔍</span>
                                {modalSearchFilter && (
                                    <button
                                        onClick={() => setModalSearchFilter("")}
                                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                                    >
                                        ✕
                                    </button>
                                )}
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Filter By
                            </label>
                            <select
                                value={modalFilterType}
                                onChange={(e) => setModalFilterType(e.target.value)}
                                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                            >
                                <option value="all">All Fields</option>
                                <option value="name">Name/Username</option>
                                <option value="department">Department</option>
                                <option value="team">Team/Supervisor</option>
                            </select>
                        </div>
                    </div>

                    {/* Selection Mode Tabs */}
                    <div className="flex flex-wrap gap-2 mb-4">
                        <button
                            onClick={() => setModalSelectionMode("individual")}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${modalSelectionMode === "individual"
                                ? "bg-yellow-600 text-white"
                                : "bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-500"
                                }`}
                        >
                            👤 Individual Selection
                        </button>
                        <button
                            onClick={() => setModalSelectionMode("team")}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${modalSelectionMode === "team"
                                ? "bg-yellow-600 text-white"
                                : "bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-500"
                                }`}
                        >
                            👥 By Team
                        </button>
                        <button
                            onClick={() => setModalSelectionMode("department")}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${modalSelectionMode === "department"
                                ? "bg-yellow-600 text-white"
                                : "bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-500"
                                }`}
                        >
                            🏢 By Department
                        </button>
                    </div>

                    {/* Quick Actions */}
                    <div className="flex flex-wrap gap-2 mb-4">
                        <button
                            onClick={() => setSelectedTeamMembers(filteredMembers.map(m => m.userID))}
                            className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700"
                        >
                            ✅ Select All Visible ({filteredMembers.length})
                        </button>
                        <button
                            onClick={() => setSelectedTeamMembers(allMembers.map(m => m.userID))}
                            className="px-3 py-1 bg-yellow-600 text-white rounded text-sm hover:bg-yellow-700"
                        >
                            📋 Select All ({allMembers.length})
                        </button>
                        <button
                            onClick={() => setSelectedTeamMembers([])}
                            className="px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700"
                        >
                            🗑️ Clear Selection
                        </button>
                    </div>

                    {/* Selection Summary */}
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                        <strong>{selectedTeamMembers.length}</strong> of <strong>{allMembers.length}</strong> total members selected
                        {modalSearchFilter.trim() && (
                            <span> • <strong>{filteredMembers.length}</strong> members visible</span>
                        )}
                    </div>
                </div>

                {/* Selection Content Based on Mode */}
                <div className="border border-gray-200 dark:border-gray-600 rounded-lg max-h-96 overflow-y-auto">
                    {modalSelectionMode === "individual" && (
                        <div className="p-3">
                            {filteredMembers.length > 0 ? (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                    {filteredMembers.map((member, index) => (
                                        <label key={index} className="flex items-center gap-2 p-2 hover:bg-gray-50 dark:hover:bg-gray-600 rounded">
                                            <input
                                                type="checkbox"
                                                checked={selectedTeamMembers.includes(member.userID)}
                                                onChange={(e) => handleTeamMemberSelect(member.userID, e.target.checked)}
                                                className="rounded border-gray-300 text-yellow-600 focus:ring-yellow-500"
                                            />
                                            <div className="flex-1 min-w-0">
                                                <div className="font-medium text-gray-900 dark:text-gray-100 truncate">{member.name}</div>
                                                <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                                    {member.username} • {member.department || 'No Dept'} • {member.supervisor || 'No Supervisor'}
                                                </div>
                                            </div>
                                        </label>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                                    <span className="text-2xl mb-2 block">🔍</span>
                                    <p>No members match your search criteria</p>
                                </div>
                            )}
                        </div>
                    )}

                    {modalSelectionMode === "team" && (
                        <div className="divide-y divide-gray-200 dark:divide-gray-600">
                            {(() => {
                                const teamGroups = getModalTeamGroups();
                                const teamNames = Object.keys(teamGroups);

                                if (teamNames.length === 0) {
                                    return (
                                        <div className="p-3 text-center py-8 text-gray-500 dark:text-gray-400">
                                            <span className="text-2xl mb-2 block">🔍</span>
                                            <p>No teams found</p>
                                            {modalSearchFilter.trim() && (
                                                <button
                                                    onClick={() => setModalSearchFilter("")}
                                                    className="text-yellow-600 hover:underline text-sm mt-2"
                                                >
                                                    Clear search to see all teams
                                                </button>
                                            )}
                                        </div>
                                    );
                                }

                                return teamNames.map((supervisor, teamIndex) => {
                                    const teamMembers = teamGroups[supervisor];
                                    const teamKey = `team-${teamIndex}`;
                                    const isExpanded = expandedModalTeams.has(teamKey);
                                    const selectedCount = teamMembers.filter(m => selectedTeamMembers.includes(m.userID)).length;
                                    const allSelected = selectedCount === teamMembers.length && teamMembers.length > 0;
                                    const partialSelected = selectedCount > 0 && selectedCount < teamMembers.length;

                                    return (
                                        <div key={teamIndex} className="p-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                                            {/* Team Header */}
                                            <div className="flex items-center gap-3">
                                                <input
                                                    type="checkbox"
                                                    checked={allSelected}
                                                    ref={input => {
                                                        if (input) input.indeterminate = partialSelected;
                                                    }}
                                                    onChange={(e) => handleSelectEntireTeam(teamMembers, e.target.checked)}
                                                    className="rounded border-gray-300 text-yellow-600 focus:ring-yellow-500"
                                                    title={allSelected ? "Deselect entire team" : "Select entire team"}
                                                />
                                                <button
                                                    onClick={() => toggleModalTeamExpansion(teamKey)}
                                                    className="flex items-center gap-2 flex-1 text-left p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                                                >
                                                    <span className={`transform transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}>
                                                        ▶
                                                    </span>
                                                    <div className="flex-1">
                                                        <div className="font-medium text-gray-900 dark:text-gray-100 flex items-center gap-2">
                                                            <span>👥</span>
                                                            {supervisor}
                                                        </div>
                                                        <div className="text-sm text-gray-500 dark:text-gray-400">
                                                            {selectedCount}/{teamMembers.length} member{teamMembers.length !== 1 ? 's' : ''} selected
                                                        </div>
                                                    </div>
                                                </button>
                                            </div>

                                            {/* Expanded Team Members */}
                                            {isExpanded && (
                                                <div className="ml-8 mt-3">
                                                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                                                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                                                            {teamMembers.map((member, memberIndex) => (
                                                                <label key={memberIndex} className="flex items-center gap-2 p-2 hover:bg-gray-100 dark:hover:bg-gray-600 rounded text-sm transition-colors cursor-pointer">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={selectedTeamMembers.includes(member.userID)}
                                                                        onChange={(e) => handleTeamMemberSelect(member.userID, e.target.checked)}
                                                                        className="rounded border-gray-300 text-yellow-600 focus:ring-yellow-500"
                                                                    />
                                                                    <div className="flex-1 min-w-0">
                                                                        <div className="font-medium text-gray-700 dark:text-gray-300 truncate">
                                                                            {member.name}
                                                                        </div>
                                                                        <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                                                            {member.username}
                                                                            {member.department && ` • ${member.department}`}
                                                                        </div>
                                                                    </div>
                                                                </label>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                });
                            })()}
                        </div>
                    )}

                    {modalSelectionMode === "department" && (
                        <div className="divide-y divide-gray-200 dark:divide-gray-600">
                            {Object.entries(deptGroups).map(([department, deptMembers], deptIndex) => {
                                const deptKey = `dept-${deptIndex}`;
                                const isExpanded = expandedModalTeams.has(deptKey);
                                const selectedCount = deptMembers.filter(m => selectedTeamMembers.includes(m.userID)).length;
                                const allSelected = selectedCount === deptMembers.length;
                                const partialSelected = selectedCount > 0 && selectedCount < deptMembers.length;

                                return (
                                    <div key={deptIndex} className="p-3">
                                        <div className="flex items-center gap-3 mb-2">
                                            <input
                                                type="checkbox"
                                                checked={allSelected}
                                                ref={input => {
                                                    if (input) input.indeterminate = partialSelected;
                                                }}
                                                onChange={(e) => handleSelectEntireDepartment(deptMembers, e.target.checked)}
                                                className="rounded border-gray-300 text-yellow-600 focus:ring-yellow-500"
                                            />
                                            <button
                                                onClick={() => toggleModalTeamExpansion(deptKey)}
                                                className="flex items-center gap-2 flex-1 text-left hover:bg-gray-50 dark:hover:bg-gray-600 rounded p-1"
                                            >
                                                <span className={`transform transition-transform ${isExpanded ? 'rotate-90' : ''}`}>▶</span>
                                                <div>
                                                    <div className="font-medium text-gray-900 dark:text-gray-100">
                                                        🏢 {department}
                                                    </div>
                                                    <div className="text-sm text-gray-500 dark:text-gray-400">
                                                        {selectedCount}/{deptMembers.length} members selected
                                                    </div>
                                                </div>
                                            </button>
                                        </div>

                                        {isExpanded && (
                                            <div className="ml-6 grid grid-cols-1 lg:grid-cols-2 gap-1">
                                                {deptMembers.map((member, memberIndex) => (
                                                    <label key={memberIndex} className="flex items-center gap-2 p-1 hover:bg-gray-50 dark:hover:bg-gray-600 rounded text-sm">
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedTeamMembers.includes(member.userID)}
                                                            onChange={(e) => handleTeamMemberSelect(member.userID, e.target.checked)}
                                                            className="rounded border-gray-300 text-yellow-600 focus:ring-yellow-500"
                                                        />
                                                        <span className="text-gray-700 dark:text-gray-300">{member.name}</span>
                                                        <span className="text-gray-500 dark:text-gray-400 text-xs">({member.department})</span>
                                                    </label>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // ============================================================================
    // STEP 6: UPDATE YOUR MODAL TO USE THE NEW SELECTION
    // ============================================================================
    // Find your existing renderAddTrainingModal function and UPDATE the team selection part:

    // FIND THIS SECTION in your renderAddTrainingModal:
    {/* Team Member Selection */ }


    // REPLACE THE ENTIRE SECTION ABOVE WITH:
    { renderEnhancedTeamSelection() }

    const renderCompletionModal = () => (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-xl font-bold text-gray-900 dark:text-white">🎯 Complete Training</h2>
                        <button
                            onClick={() => {
                                setShowCompletionModal(false);
                                setCompletionData(null);
                                setCompletionImage(null);
                                setImagePreview(null);
                                setUploadingProof(false);
                            }}
                            className="text-gray-500 hover:text-gray-700"
                            disabled={uploadingProof}
                        >
                            ×
                        </button>
                    </div>



                    <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center mb-6">
                        {imagePreview ? (
                            <div className="space-y-4">
                                <img
                                    src={imagePreview}
                                    alt="Completion proof"
                                    className="max-w-full max-h-48 mx-auto rounded-lg shadow-md"
                                />
                                <div className="text-sm text-gray-600 dark:text-gray-400">
                                    <p className="font-medium">{completionImage?.name}</p>
                                    <p>{(completionImage?.size / 1024 / 1024).toFixed(2)} MB</p>
                                    {completionImage?.type === 'image/jpeg' && (
                                        <p className="text-green-600 dark:text-green-400 text-xs">✅ Compressed</p>
                                    )}
                                </div>
                                <button
                                    onClick={() => {
                                        setCompletionImage(null);
                                        setImagePreview(null);
                                    }}
                                    className="text-red-600 hover:text-red-700 text-sm"
                                    disabled={uploadingProof}
                                >
                                    Remove Image
                                </button>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {uploadingProof ? (
                                    <>
                                        <div className="text-4xl text-yellow-500">⏳</div>
                                        <div>
                                            <p className="text-yellow-600 dark:text-yellow-400 mb-2">
                                                Processing image...
                                            </p>
                                            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                                                <div className="bg-yellow-600 h-2 rounded-full animate-pulse" style={{ width: '60%' }}></div>
                                            </div>
                                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                                Compressing and optimizing image
                                            </p>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className="text-4xl text-gray-400">📁</div>
                                        <div>
                                            <p className="text-gray-600 dark:text-gray-400 mb-2">
                                                Click to upload completion proof
                                            </p>
                                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                                JPEG, PNG, WEBP (Max 10MB)
                                            </p>
                                            <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-1">
                                                Images will be automatically compressed
                                            </p>
                                        </div>
                                    </>
                                )}

                                <input
                                    type="file"
                                    accept="image/jpeg,image/jpg,image/png,image/webp"
                                    onChange={handleImageSelect}
                                    className="hidden"
                                    id="proof-upload"
                                    disabled={uploadingProof}
                                />

                                {!uploadingProof && (
                                    <button
                                        onClick={() => document.getElementById('proof-upload').click()}
                                        className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors"
                                    >
                                        Choose Image
                                    </button>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Image Upload Tips */}
                    <div className="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3 mb-6">
                        <h4 className="text-sm font-medium text-yellow-800 dark:text-yellow-200 mb-2">📋 Upload Tips:</h4>
                        <ul className="text-xs text-yellow-700 dark:text-yellow-300 space-y-1">
                            <li>• Screenshots and photos work best</li>
                            <li>• Images are automatically compressed to reduce upload time</li>
                            <li>• Supported formats: JPEG, PNG, WEBP</li>
                            <li>• Maximum file size: 10MB (before compression)</li>
                        </ul>
                    </div>

                    <div className="flex justify-end gap-3">
                        <button
                            onClick={() => {
                                setShowCompletionModal(false);
                                setCompletionData(null);
                                setCompletionImage(null);
                                setImagePreview(null);
                                setUploadingProof(false);
                            }}
                            disabled={uploadingProof}
                            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleCompletionSubmit}
                            disabled={!completionImage || uploadingProof}
                            className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            {uploadingProof ? (
                                <>
                                    <span className="animate-spin">⏳</span>
                                    Submitting...
                                </>
                            ) : (
                                <>
                                    <span>🎯</span>
                                    Complete Training
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderFilterSection = () => (
        <div className="mb-6 bg-white dark:bg-gray-800 p-4 rounded-lg shadow border border-gray-200 dark:border-gray-700">
            <div className="flex flex-col md:flex-row gap-4 items-start md:items-end">
                {/* Search Input */}
                <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        🔍 Search Teams & Members
                    </label>
                    <div className="relative">
                        <input
                            type="text"
                            value={searchFilter}
                            onChange={(e) => setSearchFilter(e.target.value)}
                            placeholder="Search by team name, member name, username, or department..."
                            className="w-full px-4 py-2 pl-10 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
                        />
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <span className="text-gray-400 dark:text-gray-500">🔍</span>
                        </div>
                        {searchFilter && (
                            <button
                                onClick={() => setSearchFilter("")}
                                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                title="Clear search"
                            >
                                ✕
                            </button>
                        )}
                    </div>
                </div>

                {/* Filter Type Selector */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Filter Type
                    </label>
                    <select
                        value={filterType}
                        onChange={(e) => setFilterType(e.target.value)}
                        className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    >
                        <option value="all">All (Teams & Members)</option>
                        <option value="teams">Teams Only</option>
                        <option value="members">Members Only</option>
                    </select>
                </div>

                {/* Search Stats */}
                <div className="text-sm text-gray-600 dark:text-gray-400">
                    {(() => {
                        const filteredGroups = getFilteredTeamGroups();
                        const teamCount = Object.keys(filteredGroups).length;
                        const memberCount = Object.values(filteredGroups).reduce((sum, members) => sum + members.length, 0);

                        if (searchFilter.trim()) {
                            return (
                                <div className="text-center">
                                    <div className="font-medium text-gray-900 dark:text-gray-100">
                                        {teamCount} team{teamCount !== 1 ? 's' : ''}, {memberCount} member{memberCount !== 1 ? 's' : ''}
                                    </div>
                                    <div className="text-xs">matching "{searchFilter}"</div>
                                </div>
                            );
                        } else {
                            return (
                                <div className="text-center">
                                    <div className="font-medium text-gray-900 dark:text-gray-100">
                                        {teamCount} team{teamCount !== 1 ? 's' : ''}, {memberCount} member{memberCount !== 1 ? 's' : ''}
                                    </div>
                                    <div className="text-xs">total</div>
                                </div>
                            );
                        }
                    })()}
                </div>
            </div>

            {/* Active Filter Indicator */}
            {(searchFilter.trim() || filterType !== "all") && (
                <div className="mt-3 flex flex-wrap gap-2">
                    {searchFilter.trim() && (
                        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200">
                            Search: "{searchFilter}"
                            <button
                                onClick={() => setSearchFilter("")}
                                className="ml-1 text-yellow-600 dark:text-yellow-400 hover:text-yellow-800 dark:hover:text-yellow-200"
                            >
                                ✕
                            </button>
                        </span>
                    )}
                    {filterType !== "all" && (
                        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200">
                            Filter: {filterType === "teams" ? "Teams Only" : "Members Only"}
                            <button
                                onClick={() => setFilterType("all")}
                                className="ml-1 text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-200"
                            >
                                ✕
                            </button>
                        </span>
                    )}
                    <button
                        onClick={() => {
                            setSearchFilter("");
                            setFilterType("all");
                        }}
                        className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600"
                    >
                        Clear All Filters
                    </button>
                </div>
            )}
        </div>
    );

    // =============================================================================
    // PROOF VIEWING FUNCTIONS  
    // =============================================================================

    const renderCompletionProofViewer = (activity) => {
        if (!activity.completionProof) return null;

        return (
            <div className="mt-1">
                <button
                    onClick={() => {
                        setSelectedProofImage(activity.completionProof.image);
                        setShowProofModal(true);
                    }}
                    className="text-xs text-yellow-600 dark:text-yellow-400 hover:underline"
                >
                    View Proof
                </button>
            </div>
        );
    };

    const renderProofModal = () => (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl max-h-[90vh] overflow-auto">
                <div className="p-4">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                            Completion Proof
                        </h3>
                        <button
                            onClick={() => {
                                setShowProofModal(false);
                                setSelectedProofImage(null);
                            }}
                            className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                        >
                            <span className="text-2xl">×</span>
                        </button>
                    </div>

                    {selectedProofImage && (
                        <img
                            src={selectedProofImage}
                            alt="Completion proof"
                            className="max-w-full max-h-[70vh] mx-auto rounded-lg shadow-md"
                            style={{ objectFit: 'contain' }}
                        />
                    )}
                </div>
            </div>
        </div>
    );

    // =============================================================================
    // RENDER FUNCTIONS
    // =============================================================================

    const renderAddTrainingModal = () => (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Add Training to Team Members</h2>
                        <button
                            onClick={handleCloseAddTrainingModal}
                            className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                        >
                            <span className="text-2xl">×</span>
                        </button>
                    </div>

                    {/* Team Member Selection */}
                    <div className="mb-6">
                        <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">Select Team Members</h3>

                        {/* Enhanced Search and Selection Mode */}
                        <div className="mb-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border">
                            {/* Search Bar */}
                            <div className="flex flex-col lg:flex-row gap-3 mb-4">
                                <div className="flex-1">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        🔍 Search Members
                                    </label>
                                    <div className="relative">
                                        <input
                                            type="text"
                                            value={modalSearchFilter}
                                            onChange={(e) => setModalSearchFilter(e.target.value)}
                                            placeholder="Search by name, username, department, or team..."
                                            className="w-full px-3 py-2 pl-9 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                        />
                                        <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400">🔍</span>
                                        {modalSearchFilter && (
                                            <button
                                                onClick={() => setModalSearchFilter("")}
                                                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                                            >
                                                ✕
                                            </button>
                                        )}
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Filter By
                                    </label>
                                    <select
                                        value={modalFilterType}
                                        onChange={(e) => setModalFilterType(e.target.value)}
                                        className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                    >
                                        <option value="all">All Fields</option>
                                        <option value="name">Name/Username</option>
                                        <option value="department">Department</option>
                                        <option value="team">Team/Supervisor</option>
                                    </select>
                                </div>
                            </div>

                            {/* Selection Mode Tabs */}
                            <div className="flex flex-wrap gap-2 mb-4">
                                <button
                                    onClick={() => setModalSelectionMode("individual")}
                                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${modalSelectionMode === "individual"
                                        ? "bg-yellow-600 text-white"
                                        : "bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-500"
                                        }`}
                                >
                                    👤 Individual Selection
                                </button>
                                <button
                                    onClick={() => setModalSelectionMode("team")}
                                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${modalSelectionMode === "team"
                                        ? "bg-yellow-600 text-white"
                                        : "bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-500"
                                        }`}
                                >
                                    👥 By Team
                                </button>
                                <button
                                    onClick={() => setModalSelectionMode("department")}
                                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${modalSelectionMode === "department"
                                        ? "bg-yellow-600 text-white"
                                        : "bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-500"
                                        }`}
                                >
                                    🏢 By Department
                                </button>
                            </div>

                            {/* Quick Actions */}
                            <div className="flex flex-wrap gap-2 mb-4">
                                <button
                                    onClick={() => setSelectedTeamMembers(getFilteredModalMembers().map(m => m.userID))}
                                    className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700"
                                >
                                    ✅ Select All Visible ({getFilteredModalMembers().length})
                                </button>
                                <button
                                    onClick={() => setSelectedTeamMembers((records || []).map(m => m.userID))}
                                    className="px-3 py-1 bg-yellow-600 text-white rounded text-sm hover:bg-yellow-700"
                                >
                                    📋 Select All ({(records || []).length})
                                </button>
                                <button
                                    onClick={() => setSelectedTeamMembers([])}
                                    className="px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700"
                                >
                                    🗑️ Clear Selection
                                </button>
                            </div>

                            {/* Selection Summary */}
                            <div className="text-sm text-gray-600 dark:text-gray-400">
                                <strong>{selectedTeamMembers.length}</strong> of <strong>{(records || []).length}</strong> total members selected
                                {modalSearchFilter.trim() && (
                                    <span> • <strong>{getFilteredModalMembers().length}</strong> members visible</span>
                                )}
                            </div>
                        </div>

                        {/* Selection Content Based on Mode */}
                        <div className="border border-gray-200 dark:border-gray-600 rounded-lg max-h-96 overflow-y-auto">
                            {modalSelectionMode === "individual" && (
                                <div className="p-3">
                                    {getFilteredModalMembers().length > 0 ? (
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                            {getFilteredModalMembers().map((member, index) => (
                                                <label key={index} className="flex items-center gap-2 p-2 hover:bg-gray-50 dark:hover:bg-gray-600 rounded">
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedTeamMembers.includes(member.userID)}
                                                        onChange={(e) => handleTeamMemberSelect(member.userID, e.target.checked)}
                                                        className="rounded border-gray-300 text-yellow-600 focus:ring-yellow-500"
                                                    />
                                                    <div className="flex-1 min-w-0">
                                                        <div className="font-medium text-gray-900 dark:text-gray-100 truncate">{member.name}</div>
                                                        <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                                            {member.username} • {member.department || 'No Dept'} • {member.supervisor || 'No Supervisor'}
                                                        </div>
                                                    </div>
                                                </label>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                                            <span className="text-2xl mb-2 block">🔍</span>
                                            <p>No members match your search criteria</p>
                                        </div>
                                    )}
                                </div>
                            )}

                            {modalSelectionMode === "team" && (
                                <div className="divide-y divide-gray-200 dark:divide-gray-600">
                                    {(() => {
                                        const teamGroups = getModalTeamGroups();
                                        const teamNames = Object.keys(teamGroups);

                                        if (teamNames.length === 0) {
                                            return (
                                                <div className="p-3 text-center py-8 text-gray-500 dark:text-gray-400">
                                                    <span className="text-2xl mb-2 block">🔍</span>
                                                    <p>No teams found</p>
                                                    {modalSearchFilter.trim() && (
                                                        <button
                                                            onClick={() => setModalSearchFilter("")}
                                                            className="text-yellow-600 hover:underline text-sm mt-2"
                                                        >
                                                            Clear search to see all teams
                                                        </button>
                                                    )}
                                                </div>
                                            );
                                        }

                                        return teamNames.map((supervisor, teamIndex) => {
                                            const teamMembers = teamGroups[supervisor];
                                            const teamKey = `team-${teamIndex}`;
                                            const isExpanded = expandedModalTeams.has(teamKey);
                                            const selectedCount = teamMembers.filter(m => selectedTeamMembers.includes(m.userID)).length;
                                            const allSelected = selectedCount === teamMembers.length && teamMembers.length > 0;
                                            const partialSelected = selectedCount > 0 && selectedCount < teamMembers.length;

                                            return (
                                                <div key={teamIndex} className="p-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                                                    {/* Team Header */}
                                                    <div className="flex items-center gap-3">
                                                        <input
                                                            type="checkbox"
                                                            checked={allSelected}
                                                            ref={input => {
                                                                if (input) input.indeterminate = partialSelected;
                                                            }}
                                                            onChange={(e) => handleSelectEntireTeam(teamMembers, e.target.checked)}
                                                            className="rounded border-gray-300 text-yellow-600 focus:ring-yellow-500"
                                                            title={allSelected ? "Deselect entire team" : "Select entire team"}
                                                        />
                                                        <button
                                                            onClick={() => toggleModalTeamExpansion(teamKey)}
                                                            className="flex items-center gap-2 flex-1 text-left p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                                                        >
                                                            <span className={`transform transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}>
                                                                ▶
                                                            </span>
                                                            <div className="flex-1">
                                                                <div className="font-medium text-gray-900 dark:text-gray-100 flex items-center gap-2">
                                                                    <span>👥</span>
                                                                    {supervisor}
                                                                </div>
                                                                <div className="text-sm text-gray-500 dark:text-gray-400">
                                                                    {selectedCount}/{teamMembers.length} member{teamMembers.length !== 1 ? 's' : ''} selected
                                                                </div>
                                                            </div>
                                                        </button>
                                                    </div>

                                                    {/* Expanded Team Members */}
                                                    {isExpanded && (
                                                        <div className="ml-8 mt-3">
                                                            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                                                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                                                                    {teamMembers.map((member, memberIndex) => (
                                                                        <label key={memberIndex} className="flex items-center gap-2 p-2 hover:bg-gray-100 dark:hover:bg-gray-600 rounded text-sm transition-colors cursor-pointer">
                                                                            <input
                                                                                type="checkbox"
                                                                                checked={selectedTeamMembers.includes(member.userID)}
                                                                                onChange={(e) => handleTeamMemberSelect(member.userID, e.target.checked)}
                                                                                className="rounded border-gray-300 text-yellow-600 focus:ring-yellow-500"
                                                                            />
                                                                            <div className="flex-1 min-w-0">
                                                                                <div className="font-medium text-gray-700 dark:text-gray-300 truncate">
                                                                                    {member.name}
                                                                                </div>
                                                                                <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                                                                    {member.username}
                                                                                    {member.department && ` • ${member.department}`}
                                                                                </div>
                                                                            </div>
                                                                        </label>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        });
                                    })()}
                                </div>
                            )}

                            {modalSelectionMode === "department" && (
                                <div className="divide-y divide-gray-200 dark:divide-gray-600">
                                    {(() => {
                                        const deptGroups = getDepartmentGroups();
                                        const deptNames = Object.keys(deptGroups);

                                        if (deptNames.length === 0) {
                                            return (
                                                <div className="p-3 text-center py-8 text-gray-500 dark:text-gray-400">
                                                    <span className="text-2xl mb-2 block">🔍</span>
                                                    <p>No departments found</p>
                                                    {modalSearchFilter.trim() && (
                                                        <button
                                                            onClick={() => setModalSearchFilter("")}
                                                            className="text-yellow-600 hover:underline text-sm mt-2"
                                                        >
                                                            Clear search to see all departments
                                                        </button>
                                                    )}
                                                </div>
                                            );
                                        }

                                        return deptNames.map((department, deptIndex) => {
                                            const deptMembers = deptGroups[department];
                                            const deptKey = `dept-${deptIndex}`;
                                            const isExpanded = expandedModalTeams.has(deptKey);
                                            const selectedCount = deptMembers.filter(m => selectedTeamMembers.includes(m.userID)).length;
                                            const allSelected = selectedCount === deptMembers.length && deptMembers.length > 0;
                                            const partialSelected = selectedCount > 0 && selectedCount < deptMembers.length;

                                            return (
                                                <div key={deptIndex} className="p-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                                                    <div className="flex items-center gap-3 mb-2">
                                                        <input
                                                            type="checkbox"
                                                            checked={allSelected}
                                                            ref={input => {
                                                                if (input) input.indeterminate = partialSelected;
                                                            }}
                                                            onChange={(e) => handleSelectEntireDepartment(deptMembers, e.target.checked)}
                                                            className="rounded border-gray-300 text-yellow-600 focus:ring-yellow-500"
                                                        />
                                                        <button
                                                            onClick={() => toggleModalTeamExpansion(deptKey)}
                                                            className="flex items-center gap-2 flex-1 text-left hover:bg-gray-50 dark:hover:bg-gray-600 rounded p-1"
                                                        >
                                                            <span className={`transform transition-transform ${isExpanded ? 'rotate-90' : ''}`}>▶</span>
                                                            <div>
                                                                <div className="font-medium text-gray-900 dark:text-gray-100">
                                                                    🏢 {department}
                                                                </div>
                                                                <div className="text-sm text-gray-500 dark:text-gray-400">
                                                                    {selectedCount}/{deptMembers.length} members selected
                                                                </div>
                                                            </div>
                                                        </button>
                                                    </div>

                                                    {isExpanded && (
                                                        <div className="ml-6 grid grid-cols-1 lg:grid-cols-2 gap-1">
                                                            {deptMembers.map((member, memberIndex) => (
                                                                <label key={memberIndex} className="flex items-center gap-2 p-1 hover:bg-gray-50 dark:hover:bg-gray-600 rounded text-sm">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={selectedTeamMembers.includes(member.userID)}
                                                                        onChange={(e) => handleTeamMemberSelect(member.userID, e.target.checked)}
                                                                        className="rounded border-gray-300 text-yellow-600 focus:ring-yellow-500"
                                                                    />
                                                                    <span className="text-gray-700 dark:text-gray-300">{member.name}</span>
                                                                    <span className="text-gray-500 dark:text-gray-400 text-xs">({member.department})</span>
                                                                </label>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        });
                                    })()}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Training Form Fields */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Training Activity *</label>
                            <input
                                type="text"
                                value={trainingForm.activity}
                                onChange={(e) => handleTrainingFormChange('activity', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                placeholder="Enter training activity name"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Expected Outcome</label>
                            <input
                                type="text"
                                value={trainingForm.expectedOutcome}
                                onChange={(e) => handleTrainingFormChange('expectedOutcome', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                placeholder="Expected outcome"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
                            <select
                                value={trainingForm.status}
                                onChange={(e) => handleTrainingFormChange('status', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                            >
                                <option value="Scheduled">Scheduled</option>
                                <option value="In Progress">In Progress</option>
                                <option value="Attended">Attended</option>
                                <option value="Cancelled">Cancelled</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Priority</label>
                            <select
                                value={trainingForm.priority}
                                onChange={(e) => handleTrainingFormChange('priority', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                            >
                                <option value="Low">Low</option>
                                <option value="Medium">Medium</option>
                                <option value="High">High</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Type</label>
                            <select
                                value={trainingForm.type}
                                onChange={(e) => handleTrainingFormChange('type', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                            >
                                <option value="Internal">Internal</option>
                                <option value="External">External</option>
                                <option value="LMS">LMS</option>

                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Event Category</label>
                            <select
                                value={trainingForm.eventCategory}
                                onChange={(e) => handleTrainingFormChange('eventCategory', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                            >
                                <option value="Leadership Training">Leadership Training</option>
                                <option value="Process Training">Process Training</option>
                                <option value="Functional Training">Functional Training</option>
                                <option value="Behavioral Training">Behavioral Training</option>
                                <option value="Technical Training">Technical Training</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Amount</label>
                            <input
                                type="text"
                                value={trainingForm.amount}
                                onChange={(e) => handleTrainingFormChange('amount', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                placeholder="Training cost"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Lock-in Period</label>
                            <input
                                type="text"
                                value={trainingForm.lockin}
                                onChange={(e) => handleTrainingFormChange('lockin', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                placeholder="Lock-in period"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Validity</label>
                            <input
                                type="text"
                                value={trainingForm.validity}
                                onChange={(e) => handleTrainingFormChange('validity', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                placeholder="Validity period"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Start Date</label>
                            <input
                                type="date"
                                value={trainingForm.startdate}
                                onChange={(e) => handleTrainingFormChange('startdate', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">End Date</label>
                            <input
                                type="date"
                                value={trainingForm.enddate}
                                onChange={(e) => handleTrainingFormChange('enddate', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                            />
                        </div>

                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Training Links</label>
                            <input
                                type="url"
                                value={trainingForm.links}
                                onChange={(e) => handleTrainingFormChange('links', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                placeholder="https://training-link.com"
                            />
                        </div>
                    </div>

                    {/* Submit Buttons */}
                    <div className="flex justify-end gap-3">
                        <button
                            onClick={handleCloseAddTrainingModal}
                            disabled={addingTraining}
                            className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSubmitTraining}
                            disabled={addingTraining || !trainingForm.activity.trim() || selectedTeamMembers.length === 0}
                            className="px-6 py-2 bg-yellow-600 dark:bg-yellow-500 text-white rounded-lg hover:bg-yellow-700 dark:hover:bg-yellow-600 disabled:opacity-50 flex items-center gap-2"
                        >
                            {addingTraining ? (
                                <>
                                    <span className="animate-spin">⏳</span>
                                    Adding Training...
                                </>
                            ) : (
                                <>
                                    <span>📚</span>
                                    Add Training
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderProgressSummary = () => {
        // Calculate team overall progress
        const calculateTeamOverallProgress = () => {
            if (!records || records.length === 0) return 0;

            const teamMemberAverages = records.map(record => {
                const activities = record.developmentalActivities || [];
                if (activities.length === 0) return 0;

                const totalPercentage = activities.reduce((sum, activity) => {
                    return sum + parseInt(activity.percentage?.replace('%', '') || '0');
                }, 0);

                return totalPercentage / activities.length;  // ← Removed Math.round
            });

            const teamOverallPercentage = teamMemberAverages.length > 0
                ? (teamMemberAverages.reduce((sum, avg) => sum + avg, 0) / teamMemberAverages.length).toFixed(2)  // ← Shows 2 decimal places
                : "0.00";

            return {
                teamOverallPercentage,
                teamMemberAverages,
                totalMembers: records.length,
                membersWithActivities: records.filter(r => (r.developmentalActivities || []).length > 0).length
            };
        };

        const teamStats = calculateTeamOverallProgress();
        const { teamOverallPercentage, totalMembers, membersWithActivities } = teamStats;
        const progressColor = getProgressColor(teamOverallPercentage);
        const progressMessage = getProgressMessage(teamOverallPercentage);

        return (
            <div className="mb-8">
                <h3 className="text-xl font-semibold mb-2 text-gray-900 dark:text-white">Progress Summary</h3>

                {/* Team Overall Progress - Prominent Card */}
                {/* Team Overall Progress Card - Consistent Design */}
                <div className="mb-6">
                    <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow border border-gray-200 dark:border-gray-700">
                        <h4 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white flex items-center gap-2">
                            <span>🏆</span> Team Overall Progress
                        </h4>

                        {(() => {
                            // Calculate team overall progress - NEW METHOD: Average of ALL activities
                            if (!records || records.length === 0) {
                                return (
                                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                                        <span className="text-4xl mb-2 block">👥</span>
                                        <p>No team members found</p>
                                    </div>
                                );
                            }

                            // NEW: Collect ALL activities from ALL records
                            const allActivities = records.flatMap(record => record.developmentalActivities || []);

                            if (allActivities.length === 0) {
                                return (
                                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                                        <span className="text-4xl mb-2 block">📚</span>
                                        <p>No activities found</p>
                                    </div>
                                );
                            }

                            // NEW: Calculate as average of ALL activity percentages
                            const totalPercentage = allActivities.reduce((sum, activity) => {
                                return sum + parseInt(activity.percentage?.replace('%', '') || '0');
                            }, 0);

                            const teamOverallPercentage = totalPercentage / allActivities.length;
                            const membersWithActivities = records.filter(r => (r.developmentalActivities || []).length > 0).length;
                            const progressColor = getProgressColor(teamOverallPercentage);
                            const progressMessage = getProgressMessage(teamOverallPercentage);

                            return (
                                <div className="text-center">
                                    {/* Large Percentage Display */}
                                    <div className={`text-8xl font-bold mb-4 ${progressColor}`}>
                                        {parseFloat(teamOverallPercentage).toFixed(2)}%
                                    </div>

                                    {/* Progress Message */}
                                    <div className="mb-6">
                                        <p className={`text-xl font-semibold ${progressColor} mb-2`}>
                                            {progressMessage}
                                        </p>
                                        <p className="text-gray-600 dark:text-gray-400">
                                            Based on {allActivities.length} total activities from {membersWithActivities} of {records.length} team members
                                        </p>
                                    </div>

                                    {/* Progress Bar */}
                                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-4 mb-4">
                                        <div
                                            className="bg-gradient-to-r from-yellow-500 to-green-500 h-4 rounded-full transition-all duration-500 ease-in-out"
                                            style={{ width: `${teamOverallPercentage}%` }}
                                        ></div>
                                    </div>

                                    {/* Team Performance Breakdown */}
                                    <div className="grid grid-cols-4 gap-4 mt-6 pt-4 border-t border-gray-200 dark:border-gray-600">
                                        <div>
                                            <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                                                {records.filter(r => {
                                                    const activities = r.developmentalActivities || [];
                                                    const avg = activities.length > 0 ? Math.round(
                                                        activities.reduce((sum, a) => sum + parseInt(a.percentage?.replace('%', '') || '0'), 0) / activities.length
                                                    ) : 0;
                                                    return avg >= 80;
                                                }).length}
                                            </div>
                                            <div className="text-sm text-gray-500 dark:text-gray-400">Excellent</div>
                                        </div>
                                        <div>
                                            <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                                                {records.filter(r => {
                                                    const activities = r.developmentalActivities || [];
                                                    const avg = activities.length > 0 ? Math.round(
                                                        activities.reduce((sum, a) => sum + parseInt(a.percentage?.replace('%', '') || '0'), 0) / activities.length
                                                    ) : 0;
                                                    return avg >= 60 && avg < 80;
                                                }).length}
                                            </div>
                                            <div className="text-sm text-gray-500 dark:text-gray-400">Good</div>
                                        </div>
                                        <div>
                                            <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                                                {records.filter(r => {
                                                    const activities = r.developmentalActivities || [];
                                                    const avg = activities.length > 0 ? Math.round(
                                                        activities.reduce((sum, a) => sum + parseInt(a.percentage?.replace('%', '') || '0'), 0) / activities.length
                                                    ) : 0;
                                                    return avg >= 40 && avg < 60;
                                                }).length}
                                            </div>
                                            <div className="text-sm text-gray-500 dark:text-gray-400">Fair</div>
                                        </div>
                                        <div>
                                            <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                                                {records.filter(r => {
                                                    const activities = r.developmentalActivities || [];
                                                    const avg = activities.length > 0 ? Math.round(
                                                        activities.reduce((sum, a) => sum + parseInt(a.percentage?.replace('%', '') || '0'), 0) / activities.length
                                                    ) : 0;
                                                    return avg < 40;
                                                }).length}
                                            </div>
                                            <div className="text-sm text-gray-500 dark:text-gray-400">Needs Attention</div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })()}
                    </div>
                </div>
                {renderFilterSection()}

                {/* Existing Progress Summary Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

                    {/* Left Side - Developmental Activities Type Progress */}
                    <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow border border-gray-200 dark:border-gray-700">
                        <h4 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white flex items-center gap-2">
                            <span>🏷️</span> Developmental Activities Type Progress
                        </h4>

                        {(() => {
                            // Collect all activities from all team members
                            const allActivities = (records || []).flatMap(record =>
                                (record.developmentalActivities || []).map(activity => ({
                                    ...activity,
                                    ownerName: record.name,
                                    ownerUsername: record.username
                                }))
                            );

                            if (allActivities.length === 0) {
                                return (
                                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                                        <span className="text-4xl mb-2 block">🏷️</span>
                                        <p>No developmental activities found</p>
                                    </div>
                                );
                            }

                            // Group activities by type
                            const groupedByType = allActivities.reduce((acc, activity) => {
                                const type = activity.type || 'Unspecified';
                                if (!acc[type]) {
                                    acc[type] = [];
                                }
                                acc[type].push(activity);
                                return acc;
                            }, {});

                            const typeEntries = Object.entries(groupedByType);

                            return (
                                <div className="space-y-4 max-h-96 overflow-y-auto">
                                    {typeEntries.map(([type, typeActivities], index) => {
                                        // Calculate statistics for this type
                                        const totalActivities = typeActivities.length;
                                        const completedActivities = typeActivities.filter(a =>
                                            parseInt(a.percentage?.replace('%', '') || '0') === 100
                                        ).length;
                                        const inProgressActivities = typeActivities.filter(a => {
                                            const perc = parseInt(a.percentage?.replace('%', '') || '0');
                                            return perc > 0 && perc < 100;
                                        }).length;
                                        const notStartedActivities = typeActivities.filter(a =>
                                            parseInt(a.percentage?.replace('%', '') || '0') === 0
                                        ).length;

                                        // Calculate average progress for this type
                                        const avgProgress = totalActivities > 0
                                            ? Math.round(
                                                typeActivities.reduce((sum, activity) =>
                                                    sum + parseInt(activity.percentage?.replace('%', '') || '0'), 0
                                                ) / totalActivities
                                            )
                                            : 0;

                                        const progressColor = getProgressColor(avgProgress);
                                        const progressBgColor = getProgressBgColor(avgProgress);

                                        // Get unique team members for this type
                                        const uniqueMembers = [...new Set(typeActivities.map(a => a.ownerName))];
                                        const memberCount = uniqueMembers.length;

                                        return (
                                            <div key={index} className="border border-gray-200 dark:border-gray-600 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                                                {/* Type Header */}
                                                <div className="flex items-center justify-between mb-3">
                                                    <div className="flex items-center gap-3">
                                                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${type === 'External'
                                                            ? 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200'
                                                            : type === 'Internal'
                                                                ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
                                                                : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                                                            }`}>
                                                            🏷️ {type}
                                                        </span>
                                                        <div className="text-xs text-gray-500 dark:text-gray-400">
                                                            {memberCount} team member{memberCount !== 1 ? 's' : ''} • {totalActivities} activit{totalActivities !== 1 ? 'ies' : 'y'}
                                                        </div>
                                                    </div>
                                                    <div className={`text-2xl font-bold ${progressColor}`}>
                                                        {avgProgress}%
                                                    </div>
                                                </div>

                                                {/* Progress Bar */}
                                                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 mb-3">
                                                    <div
                                                        className={`h-3 rounded-full transition-all duration-500 ease-in-out ${progressBgColor}`}
                                                        style={{ width: `${avgProgress}%` }}
                                                    ></div>
                                                </div>

                                                {/* Statistics Grid */}
                                                <div className="grid grid-cols-4 gap-2 mb-3">
                                                    <div className="text-center">
                                                        <div className="text-lg font-bold text-green-600 dark:text-green-400">
                                                            {completedActivities}
                                                        </div>
                                                        <div className="text-xs text-gray-500 dark:text-gray-400">Completed</div>
                                                    </div>
                                                    <div className="text-center">
                                                        <div className="text-lg font-bold text-yellow-600 dark:text-yellow-400">
                                                            {inProgressActivities}
                                                        </div>
                                                        <div className="text-xs text-gray-500 dark:text-gray-400">In Progress</div>
                                                    </div>
                                                    <div className="text-center">
                                                        <div className="text-lg font-bold text-gray-600 dark:text-gray-400">
                                                            {notStartedActivities}
                                                        </div>
                                                        <div className="text-xs text-gray-500 dark:text-gray-400">Not Started</div>
                                                    </div>
                                                    <div className="text-center">
                                                        <div className="text-lg font-bold text-purple-600 dark:text-purple-400">
                                                            {totalActivities}
                                                        </div>
                                                        <div className="text-xs text-gray-500 dark:text-gray-400">Total</div>
                                                    </div>
                                                </div>

                                                {/* Sample Activities (Top 3) */}
                                                <div className="border-t border-gray-200 dark:border-gray-600 pt-3">
                                                    <div className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                        Recent Activities:
                                                    </div>
                                                    <div className="space-y-1">
                                                        {typeActivities.slice(0, 3).map((activity, actIndex) => {
                                                            const activityProgress = parseInt(activity.percentage?.replace('%', '') || '0');
                                                            return (
                                                                <div key={actIndex} className="flex items-center justify-between text-xs">
                                                                    <div className="flex-1 truncate">
                                                                        <span className="text-gray-700 dark:text-gray-300">
                                                                            {activity.activity || 'Untitled Activity'}
                                                                        </span>
                                                                        <span className="text-gray-500 dark:text-gray-400 ml-2">
                                                                            ({activity.ownerName})
                                                                        </span>
                                                                    </div>
                                                                    <div className="flex items-center gap-2 ml-2">
                                                                        <div className="w-12 bg-gray-200 dark:bg-gray-600 rounded-full h-1">
                                                                            <div
                                                                                className="bg-yellow-500 dark:bg-yellow-400 h-1 rounded-full"
                                                                                style={{ width: `${activityProgress}%` }}
                                                                            ></div>
                                                                        </div>
                                                                        <span className="text-xs font-medium text-gray-600 dark:text-gray-400 min-w-[25px]">
                                                                            {activityProgress}%
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                        {typeActivities.length > 3 && (
                                                            <div className="text-xs text-gray-500 dark:text-gray-400 text-center pt-1">
                                                                +{typeActivities.length - 3} more activit{typeActivities.length - 3 !== 1 ? 'ies' : 'y'}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Team Members involved */}
                                                <div className="border-t border-gray-200 dark:border-gray-600 pt-2 mt-2">
                                                    <div className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                        Team Members:
                                                    </div>
                                                    <div className="flex flex-wrap gap-1">
                                                        {uniqueMembers.slice(0, 4).map((memberName, memberIndex) => (
                                                            <span key={memberIndex} className="px-2 py-0.5 bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded text-xs">
                                                                {memberName}
                                                            </span>
                                                        ))}
                                                        {uniqueMembers.length > 4 && (
                                                            <span className="px-2 py-0.5 bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-400 rounded text-xs">
                                                                +{uniqueMembers.length - 4} more
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}

                                    {/* Summary Footer */}
                                    <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                                        <div className="grid grid-cols-2 gap-4 text-center">
                                            <div>
                                                <div className="text-xl font-bold text-gray-900 dark:text-gray-100">
                                                    {typeEntries.length}
                                                </div>
                                                <div className="text-sm text-gray-600 dark:text-gray-400">Activity Types</div>
                                            </div>
                                            <div>
                                                <div className="text-xl font-bold text-gray-900 dark:text-gray-100">
                                                    {allActivities.length}
                                                </div>
                                                <div className="text-sm text-gray-600 dark:text-gray-400">Total Activities</div>
                                            </div>
                                        </div>
                                        <div className="mt-3 text-center">
                                            <div className="text-sm text-gray-600 dark:text-gray-400">
                                                Overall Type Distribution:
                                                {typeEntries.map(([type, activities], index) => (
                                                    <span key={index} className="ml-1">
                                                        {type} ({Math.round((activities.length / allActivities.length) * 100)}%)
                                                        {index < typeEntries.length - 1 ? ',' : ''}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })()}
                    </div>

                    {/* Right Side - Individual Developmental Activities with Progress */}
                    <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow border border-gray-200 dark:border-gray-700">
                        <h4 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white flex items-center gap-2">
                            <span>📚</span> Developmental Activities Progress
                        </h4>

                        {(() => {
                            const allActivitiesWithOwner = (records || []).flatMap(record =>
                                (record.developmentalActivities || []).map(activity => ({
                                    ...activity,
                                    ownerName: record.name,
                                    ownerUsername: record.username
                                }))
                            );

                            if (allActivitiesWithOwner.length === 0) {
                                return (
                                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                                        <span className="text-4xl mb-2 block">📚</span>
                                        <p>No developmental activities found</p>
                                    </div>
                                );
                            }

                            const sortedActivities = allActivitiesWithOwner.sort((a, b) => {
                                const percentA = parseInt(a.percentage?.replace('%', '') || '0');
                                const percentB = parseInt(b.percentage?.replace('%', '') || '0');
                                return percentB - percentA;
                            });

                            return (
                                <div className="space-y-3 max-h-96 overflow-y-auto">
                                    {sortedActivities.map((activity, index) => {
                                        const percentage = parseInt(activity.percentage?.replace('%', '') || '0');
                                        const progressColor = getProgressColor(percentage);
                                        const progressBgColor = getProgressBgColor(percentage);

                                        return (
                                            <div key={index} className="border border-gray-200 dark:border-gray-600 rounded-lg p-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                                                <div className="flex items-center justify-between mb-2">
                                                    <div className="flex-1">
                                                        <h5 className="font-medium text-gray-900 dark:text-gray-100 text-sm">
                                                            {activity.activity || "Untitled Activity"}
                                                        </h5>
                                                        <p className="text-xs text-gray-500 dark:text-gray-400">
                                                            👤 {activity.ownerName}
                                                            {activity.eventCategory && (
                                                                <span className="ml-2 px-2 py-0.5 bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 rounded text-xs">
                                                                    {activity.eventCategory}
                                                                </span>
                                                            )}
                                                        </p>
                                                        {activity.type && (
                                                            <span className={`inline-block mt-1 px-2 py-0.5 rounded text-xs font-medium ${activity.type === 'External'
                                                                ? 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200'
                                                                : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                                                                }`}>
                                                                {activity.type}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className={`text-xl font-bold ${progressColor} min-w-[50px] text-right`}>
                                                        {percentage}%
                                                    </div>
                                                </div>

                                                {/* Progress Bar */}
                                                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mb-2">
                                                    <div
                                                        className={`h-2 rounded-full transition-all duration-500 ease-in-out ${progressBgColor}`}
                                                        style={{ width: `${percentage}%` }}
                                                    ></div>
                                                </div>

                                                {/* Additional Info */}
                                                <div className="flex justify-between items-center text-xs text-gray-600 dark:text-gray-400">
                                                    <div className="flex items-center gap-3">
                                                        {activity.status && (
                                                            <span className={`px-2 py-0.5 rounded text-xs ${activity.status === 'Attended' ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' :
                                                                activity.status === 'Scheduled' ? 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200' :
                                                                    activity.status === 'Cancelled' ? 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200' :
                                                                        'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                                                                }`}>
                                                                {activity.status}
                                                            </span>
                                                        )}
                                                        {activity.priority && (
                                                            <span className={`px-2 py-0.5 rounded text-xs ${activity.priority === 'High' ? 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200' :
                                                                activity.priority === 'Medium' ? 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200' :
                                                                    'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
                                                                }`}>
                                                                {activity.priority}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div>
                                                        {activity.startdate && (
                                                            <span>📅 {activity.startdate}</span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}

                                    {/* Summary */}
                                    <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
                                        <div className="flex justify-between items-center">
                                            <span className="font-medium text-gray-900 dark:text-gray-100">Total Activities</span>
                                            <span className="text-xl font-bold text-gray-900 dark:text-gray-100">{allActivitiesWithOwner.length}</span>
                                        </div>
                                        <div className="flex justify-between items-center mt-1">
                                            <span className="text-sm text-gray-600 dark:text-gray-400">Average Progress</span>
                                            <span className="text-sm font-medium text-yellow-600 dark:text-yellow-400">
                                                {allActivitiesWithOwner.length > 0 ? Math.round(
                                                    allActivitiesWithOwner.reduce((sum, activity) =>
                                                        sum + parseInt(activity.percentage?.replace('%', '') || '0'), 0) / allActivitiesWithOwner.length
                                                ) : 0}%
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            );
                        })()}
                    </div>
                </div>
            </div>
        );
    };



    // Replace the renderTeamAccordionTable function with this team-level version

    const renderTeamAccordionTable = () => {
        // Use filtered data instead of grouping directly
        const teamGroups = getFilteredTeamGroups();
        const teamNames = Object.keys(teamGroups);

        return (
            <div className="mb-6">
                <h3 className="text-lg font-semibold mb-4 text-yellow-700 dark:text-yellow-300 bg-yellow-50 dark:bg-yellow-900/30 p-2 rounded border border-yellow-200 dark:border-yellow-800">
                    Team Progress Summary by Teams
                    <span className="text-xs font-normal text-yellow-600 dark:text-yellow-400 ml-2">
                        💡 Click on teams to view their members and training details
                    </span>
                </h3>

                {/* No Results Message */}
                {teamNames.length === 0 && searchFilter.trim() && (
                    <div className="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 rounded-lg p-6 text-center mb-4">
                        <div className="text-yellow-600 dark:text-yellow-400">
                            <span className="text-2xl mb-2 block">🔍</span>
                            <h4 className="font-semibold mb-2">No results found</h4>
                            <p className="text-sm">
                                No teams or members match your search for "{searchFilter}".
                                <br />
                                Try a different search term or clear the filter.
                            </p>
                        </div>
                    </div>
                )}

                <div className="overflow-auto bg-white dark:bg-gray-800 rounded-lg shadow transition-all duration-300 border border-gray-200 dark:border-gray-700">
                    <table className="min-w-full border-collapse text-xs">
                        <thead>
                            <tr className="bg-yellow-300 dark:bg-yellow-600 text-black dark:text-white">
                                <th className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-left font-semibold">TEAM / SUPERVISOR</th>
                                <th className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-left font-semibold">TEAM SIZE</th>
                                <th className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-left font-semibold">TOTAL TRAININGS</th>
                                <th className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-left font-semibold">COMPLETED</th>
                                <th className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-left font-semibold">IN PROGRESS</th>
                                <th className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-left font-semibold">TEAM AVG PROGRESS</th>
                                <th className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-left font-semibold">TEAM STATUS</th>
                                <th className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-center font-semibold">ACTIONS</th>
                            </tr>
                        </thead>
                        <tbody>
                            {teamNames.length > 0 ? teamNames.map((supervisor, teamIndex) => {
                                const teamMembers = teamGroups[supervisor];
                                const isTeamExpanded = expandedRows.has(`team-${teamIndex}`);

                                // Calculate team-level statistics
                                const teamStats = (() => {
                                    const allActivities = teamMembers.flatMap(member => member.developmentalActivities || []);
                                    const totalTrainings = allActivities.length;
                                    const completedTrainings = allActivities.filter(a => parseInt(a.percentage?.replace('%', '') || '0') === 100).length;
                                    const inProgressTrainings = allActivities.filter(a => {
                                        const perc = parseInt(a.percentage?.replace('%', '') || '0');
                                        return perc > 0 && perc < 100;
                                    }).length;

                                    const teamMemberAverages = teamMembers.map(member => {
                                        const activities = member.developmentalActivities || [];
                                        if (activities.length === 0) return 0;
                                        return Math.round(
                                            activities.reduce((sum, a) => sum + parseInt(a.percentage?.replace('%', '') || '0'), 0) / activities.length
                                        );
                                    });

                                    const teamAvgProgress = teamMemberAverages.length > 0
                                        ? Math.round(teamMemberAverages.reduce((sum, avg) => sum + avg, 0) / teamMemberAverages.length)
                                        : 0;

                                    return {
                                        teamSize: teamMembers.length,
                                        totalTrainings,
                                        completedTrainings,
                                        inProgressTrainings,
                                        teamAvgProgress
                                    };
                                })();

                                return (
                                    <Fragment key={teamIndex}>
                                        {/* Team Summary Row */}
                                        <tr
                                            className={`cursor-pointer transition-colors ${isTeamExpanded
                                                ? 'bg-yellow-50 dark:bg-yellow-900/30'
                                                : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                                                }`}
                                            onClick={() => toggleAccordionRow(`team-${teamIndex}`)}
                                        >
                                            <td className="border border-gray-300 dark:border-gray-600 px-3 py-3">
                                                <div className="flex items-center gap-2">
                                                    <span className={`transform transition-transform duration-200 ${isTeamExpanded ? 'rotate-90' : ''}`}>
                                                        ▶
                                                    </span>
                                                    <div>
                                                        <div className="font-medium text-gray-900 dark:text-gray-100 flex items-center gap-2">
                                                            <span>👥</span>
                                                            {supervisor}
                                                        </div>
                                                        <div className="text-xs text-gray-500 dark:text-gray-400">
                                                            Team Leader/Supervisor
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="border border-gray-300 dark:border-gray-600 px-3 py-3 text-center">
                                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200">
                                                    {teamStats.teamSize} members
                                                </span>
                                            </td>
                                            <td className="border border-gray-300 dark:border-gray-600 px-3 py-3 text-center">
                                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200">
                                                    {teamStats.totalTrainings}
                                                </span>
                                            </td>
                                            <td className="border border-gray-300 dark:border-gray-600 px-3 py-3 text-center">
                                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200">
                                                    {teamStats.completedTrainings}
                                                </span>
                                            </td>
                                            <td className="border border-gray-300 dark:border-gray-600 px-3 py-3 text-center">
                                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200">
                                                    {teamStats.inProgressTrainings}
                                                </span>
                                            </td>
                                            <td className="border border-gray-300 dark:border-gray-600 px-3 py-3 text-center">
                                                <div className="flex items-center gap-2">
                                                    <div className="flex-1 bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                                                        <div
                                                            className="bg-yellow-600 dark:bg-yellow-500 h-2 rounded-full transition-all duration-300"
                                                            style={{ width: `${teamStats.teamAvgProgress}%` }}
                                                        ></div>
                                                    </div>
                                                    <span className="text-xs font-medium text-gray-700 dark:text-gray-300 min-w-[30px]">
                                                        {teamStats.teamAvgProgress}%
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="border border-gray-300 dark:border-gray-600 px-3 py-3 text-center">
                                                <span className={`px-2 py-1 rounded text-xs font-medium ${teamStats.teamAvgProgress >= 80 ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' :
                                                    teamStats.teamAvgProgress >= 60 ? 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200' :
                                                        teamStats.teamAvgProgress >= 40 ? 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200' :
                                                            'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
                                                    }`}>
                                                    {teamStats.teamAvgProgress >= 80 ? 'Excellent' :
                                                        teamStats.teamAvgProgress >= 60 ? 'Good' :
                                                            teamStats.teamAvgProgress >= 40 ? 'Fair' : 'Needs Attention'}
                                                </span>
                                            </td>
                                            <td className="border border-gray-300 dark:border-gray-600 px-3 py-3 text-center">
                                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                                    {isTeamExpanded ? 'Click to collapse' : 'Click to expand'}
                                                </span>
                                            </td>
                                        </tr>

                                        {/* Expanded Team Members Details */}
                                        {isTeamExpanded && (
                                            <tr>
                                                <td colSpan="8" className="border border-gray-300 dark:border-gray-600 p-0">
                                                    <div className="bg-gray-50 dark:bg-gray-900 p-4">
                                                        <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
                                                            <span>👥</span>
                                                            Team Members under {supervisor}
                                                            <span className="text-xs font-normal text-gray-500 dark:text-gray-400 ml-2">
                                                                💡 Click team members to view their individual training details
                                                            </span>
                                                        </h4>

                                                        {/* Team Members Accordion Table */}
                                                        <div className="overflow-x-auto">
                                                            <table className="w-full border-collapse text-xs">
                                                                <thead>
                                                                    <tr className="bg-gray-200 dark:bg-gray-700">
                                                                        <th className="border border-gray-300 dark:border-gray-600 px-2 py-2 text-left">TEAM MEMBER</th>
                                                                        <th className="border border-gray-300 dark:border-gray-600 px-2 py-2 text-left">TOTAL TRAININGS</th>
                                                                        <th className="border border-gray-300 dark:border-gray-600 px-2 py-2 text-left">COMPLETED</th>
                                                                        <th className="border border-gray-300 dark:border-gray-600 px-2 py-2 text-left">IN PROGRESS</th>
                                                                        <th className="border border-gray-300 dark:border-gray-600 px-2 py-2 text-left">AVG PROGRESS</th>
                                                                        <th className="border border-gray-300 dark:border-gray-600 px-2 py-2 text-left">STATUS</th>
                                                                        <th className="border border-gray-300 dark:border-gray-600 px-2 py-2 text-center">ACTIONS</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {teamMembers.map((member, memberIndex) => {
                                                                        const memberKey = `team-${teamIndex}-member-${memberIndex}`;
                                                                        const isMemberExpanded = expandedRows.has(memberKey);

                                                                        const activities = member.developmentalActivities || [];
                                                                        const totalActivities = activities.length;
                                                                        const completedActivities = activities.filter(a => parseInt(a.percentage?.replace('%', '') || '0') === 100).length;
                                                                        const inProgressActivities = activities.filter(a => {
                                                                            const perc = parseInt(a.percentage?.replace('%', '') || '0');
                                                                            return perc > 0 && perc < 100;
                                                                        }).length;
                                                                        const avgProgress = totalActivities > 0 ? Math.round(
                                                                            activities.reduce((sum, a) => sum + parseInt(a.percentage?.replace('%', '') || '0'), 0) / totalActivities
                                                                        ) : 0;

                                                                        return (
                                                                            <Fragment key={memberIndex}>
                                                                                {/* Team Member Row */}
                                                                                <tr
                                                                                    className={`cursor-pointer transition-colors ${isMemberExpanded
                                                                                        ? 'bg-yellow-50 dark:bg-yellow-900/30'
                                                                                        : 'hover:bg-white dark:hover:bg-gray-800'
                                                                                        }`}
                                                                                    onClick={() => toggleAccordionRow(memberKey)}
                                                                                >
                                                                                    <td className="border border-gray-300 dark:border-gray-600 px-2 py-2">
                                                                                        <div className="flex items-center gap-2">
                                                                                            <span className={`transform transition-transform duration-200 ${isMemberExpanded ? 'rotate-90' : ''}`}>
                                                                                                ▶
                                                                                            </span>
                                                                                            <div>
                                                                                                <div className="font-medium text-gray-900 dark:text-gray-100">{member.name}</div>
                                                                                                <div className="text-xs text-gray-500 dark:text-gray-400">{member.username}</div>
                                                                                            </div>
                                                                                        </div>
                                                                                    </td>
                                                                                    <td className="border border-gray-300 dark:border-gray-600 px-2 py-2 text-center">
                                                                                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200">
                                                                                            {totalActivities}
                                                                                        </span>
                                                                                    </td>
                                                                                    <td className="border border-gray-300 dark:border-gray-600 px-2 py-2 text-center">
                                                                                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200">
                                                                                            {completedActivities}
                                                                                        </span>
                                                                                    </td>
                                                                                    <td className="border border-gray-300 dark:border-gray-600 px-2 py-2 text-center">
                                                                                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200">
                                                                                            {inProgressActivities}
                                                                                        </span>
                                                                                    </td>
                                                                                    <td className="border border-gray-300 dark:border-gray-600 px-2 py-2 text-center">
                                                                                        <div className="flex items-center gap-2">
                                                                                            <div className="flex-1 bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                                                                                                <div
                                                                                                    className="bg-yellow-600 dark:bg-yellow-500 h-2 rounded-full transition-all duration-300"
                                                                                                    style={{ width: `${avgProgress}%` }}
                                                                                                ></div>
                                                                                            </div>
                                                                                            <span className="text-xs font-medium text-gray-700 dark:text-gray-300 min-w-[30px]">
                                                                                                {avgProgress}%
                                                                                            </span>
                                                                                        </div>
                                                                                    </td>
                                                                                    <td className="border border-gray-300 dark:border-gray-600 px-2 py-2 text-center">
                                                                                        <span className={`px-2 py-1 rounded text-xs font-medium ${avgProgress >= 80 ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' :
                                                                                            avgProgress >= 60 ? 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200' :
                                                                                                avgProgress >= 40 ? 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200' :
                                                                                                    'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
                                                                                            }`}>
                                                                                            {avgProgress >= 80 ? 'Excellent' :
                                                                                                avgProgress >= 60 ? 'Good' :
                                                                                                    avgProgress >= 40 ? 'Fair' : 'Needs Attention'}
                                                                                        </span>
                                                                                    </td>
                                                                                    <td className="border border-gray-300 dark:border-gray-600 px-2 py-2 text-center">
                                                                                        <span className="text-xs text-gray-500 dark:text-gray-400">
                                                                                            {isMemberExpanded ? 'Collapse' : 'Expand'}
                                                                                        </span>
                                                                                    </td>
                                                                                </tr>

                                                                                {/* Team Member Training Details */}
                                                                                {isMemberExpanded && (
                                                                                    <tr>
                                                                                        <td colSpan="7" className="border border-gray-300 dark:border-gray-600 p-0">
                                                                                            <div className="bg-gray-100 dark:bg-gray-800 p-3">
                                                                                                <h5 className="font-medium text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
                                                                                                    <span>📚</span>
                                                                                                    Training Details for {member.name}
                                                                                                </h5>

                                                                                                {activities.length > 0 ? (
                                                                                                    <div className="overflow-x-auto">
                                                                                                        <table className="w-full border-collapse text-xs">
                                                                                                            <thead>
                                                                                                                <tr className="bg-gray-200 dark:bg-gray-700">
                                                                                                                    <th className="border border-gray-300 dark:border-gray-600 px-2 py-1 text-left">TRAINING</th>
                                                                                                                    <th className="border border-gray-300 dark:border-gray-600 px-2 py-1 text-left">CATEGORY</th>
                                                                                                                    <th className="border border-gray-300 dark:border-gray-600 px-2 py-1 text-left">STATUS</th>
                                                                                                                    <th className="border border-gray-300 dark:border-gray-600 px-2 py-1 text-left">TYPE</th>
                                                                                                                    <th className="border border-gray-300 dark:border-gray-600 px-2 py-1 text-left">PRIORITY</th>
                                                                                                                    <th className="border border-gray-300 dark:border-gray-600 px-2 py-1 text-left">PERCENTAGE</th>
                                                                                                                    <th className="border border-gray-300 dark:border-gray-600 px-2 py-1 text-left">START DATE</th>
                                                                                                                    <th className="border border-gray-300 dark:border-gray-600 px-2 py-1 text-left">END DATE</th>
                                                                                                                </tr>
                                                                                                            </thead>
                                                                                                            <tbody>
                                                                                                                {activities.map((activity, activityIndex) => {
                                                                                                                    // Find the original record index for this member
                                                                                                                    const originalRecordIndex = (records || []).findIndex(r => r.userID === member.userID);

                                                                                                                    return (
                                                                                                                        <tr key={activityIndex} className="hover:bg-white dark:hover:bg-gray-700">
                                                                                                                            <td className="border border-gray-300 dark:border-gray-600 px-2 py-1">
                                                                                                                                <div className="font-medium text-gray-900 dark:text-gray-100">
                                                                                                                                    {activity.activity || "N/A"}
                                                                                                                                </div>
                                                                                                                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                                                                                                                    {activity.expectedOutcome || "No outcome specified"}
                                                                                                                                </div>
                                                                                                                            </td>
                                                                                                                            <td className="border border-gray-300 dark:border-gray-600 px-2 py-1">
                                                                                                                                <span className="px-2 py-1 rounded text-xs font-medium bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200">
                                                                                                                                    {activity.eventCategory || "N/A"}
                                                                                                                                </span>
                                                                                                                            </td>
                                                                                                                            <td className="border border-gray-300 dark:border-gray-600 px-2 py-1">
                                                                                                                                <span className={`px-2 py-1 rounded text-xs font-medium ${activity.status === 'Attended' ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' :
                                                                                                                                    activity.status === 'Scheduled' ? 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200' :
                                                                                                                                        activity.status === 'Cancelled' ? 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200' :
                                                                                                                                            'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                                                                                                                                    }`}>
                                                                                                                                    {activity.status || "N/A"}
                                                                                                                                </span>
                                                                                                                            </td>
                                                                                                                            <td className="border border-gray-300 dark:border-gray-600 px-2 py-1 text-gray-700 dark:text-gray-300">
                                                                                                                                {activity.type || "N/A"}
                                                                                                                            </td>
                                                                                                                            <td className="border border-gray-300 dark:border-gray-600 px-2 py-1">
                                                                                                                                <span className={`px-2 py-1 rounded text-xs font-medium ${activity.priority === 'High' ? 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200' :
                                                                                                                                    activity.priority === 'Medium' ? 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200' :
                                                                                                                                        activity.priority === 'Low' ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' :
                                                                                                                                            'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                                                                                                                                    }`}>
                                                                                                                                    {activity.priority || "N/A"}
                                                                                                                                </span>
                                                                                                                            </td>
                                                                                                                            <td className="border border-gray-300 dark:border-gray-600 px-2 py-1">
                                                                                                                                {editingPercentage?.recordIndex === originalRecordIndex &&
                                                                                                                                    editingPercentage?.activityIndex === activityIndex ? (
                                                                                                                                    <div className="flex items-center gap-1">
                                                                                                                                        <input
                                                                                                                                            type="text"
                                                                                                                                            value={editingPercentage.value}
                                                                                                                                            onChange={(e) => handlePercentageChange(e.target.value)}
                                                                                                                                            onKeyDown={handlePercentageKeyPress}
                                                                                                                                            className="w-12 px-1 py-0.5 text-xs border border-yellow-300 dark:border-yellow-600 rounded focus:outline-none focus:border-yellow-500 focus:ring-1 focus:ring-yellow-200 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                                                                                                                            maxLength="3"
                                                                                                                                            placeholder="0-100"
                                                                                                                                            autoFocus
                                                                                                                                            onFocus={(e) => e.target.select()}
                                                                                                                                        />
                                                                                                                                        <span className="text-xs text-gray-700 dark:text-gray-300">%</span>
                                                                                                                                        <button
                                                                                                                                            onClick={handleMonitoringPercentageSave}
                                                                                                                                            disabled={savingPercentage}
                                                                                                                                            className="px-1 py-0.5 bg-green-500 dark:bg-green-600 text-white text-xs rounded hover:bg-green-600 dark:hover:bg-green-700 disabled:opacity-50"
                                                                                                                                            title="Save"
                                                                                                                                        >
                                                                                                                                            {savingPercentage ? "..." : "✓"}
                                                                                                                                        </button>
                                                                                                                                        <button
                                                                                                                                            onClick={handlePercentageCancel}
                                                                                                                                            disabled={savingPercentage}
                                                                                                                                            className="px-1 py-0.5 bg-gray-500 dark:bg-gray-600 text-white text-xs rounded hover:bg-gray-600 dark:hover:bg-gray-700 disabled:opacity-50"
                                                                                                                                            title="Cancel"
                                                                                                                                        >
                                                                                                                                            ✕
                                                                                                                                        </button>
                                                                                                                                    </div>
                                                                                                                                ) : (
                                                                                                                                    <div>
                                                                                                                                        <div
                                                                                                                                            className="flex items-center gap-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 p-1 rounded group"
                                                                                                                                            onClick={() => handleMonitoringPercentageEdit(originalRecordIndex, activityIndex, activity.percentage || "0%", member.username)}
                                                                                                                                            title="Click to edit percentage"
                                                                                                                                        >
                                                                                                                                            <div className="flex-1 bg-gray-200 dark:bg-gray-600 rounded-full h-2 min-w-[50px]">
                                                                                                                                                <div
                                                                                                                                                    className="bg-yellow-600 dark:bg-yellow-500 h-2 rounded-full transition-all duration-300"
                                                                                                                                                    style={{ width: `${activity.percentage?.replace('%', '') || 0}%` }}
                                                                                                                                                ></div>
                                                                                                                                            </div>
                                                                                                                                            <span className="text-xs text-gray-700 dark:text-gray-300 min-w-[30px]">
                                                                                                                                                {activity.percentage || "0%"}
                                                                                                                                            </span>
                                                                                                                                            <span className="text-xs text-yellow-500 dark:text-yellow-400 opacity-0 group-hover:opacity-100 transition-opacity">✎</span>
                                                                                                                                        </div>
                                                                                                                                        {renderCompletionProofViewer(activity)}
                                                                                                                                    </div>
                                                                                                                                )}
                                                                                                                            </td>
                                                                                                                            <td className="border border-gray-300 dark:border-gray-600 px-2 py-1 text-xs text-gray-600 dark:text-gray-400">
                                                                                                                                {(() => {
                                                                                                                                    const parsedDate = parseDate(activity.startdate);
                                                                                                                                    return parsedDate
                                                                                                                                        ? parsedDate.toLocaleDateString("en-PH", {
                                                                                                                                            year: "numeric",
                                                                                                                                            month: "short",
                                                                                                                                            day: "numeric"
                                                                                                                                        })
                                                                                                                                        : "N/A";
                                                                                                                                })()}
                                                                                                                            </td>
                                                                                                                            <td className="border border-gray-300 dark:border-gray-600 px-2 py-1 text-xs text-gray-600 dark:text-gray-400">
                                                                                                                                {(() => {
                                                                                                                                    const parsedDate = parseDate(activity.enddate);
                                                                                                                                    return parsedDate
                                                                                                                                        ? parsedDate.toLocaleDateString("en-PH", {
                                                                                                                                            year: "numeric",
                                                                                                                                            month: "short",
                                                                                                                                            day: "numeric"
                                                                                                                                        })
                                                                                                                                        : "N/A";
                                                                                                                                })()}
                                                                                                                            </td>
                                                                                                                        </tr>
                                                                                                                    );
                                                                                                                })}
                                                                                                            </tbody>
                                                                                                        </table>
                                                                                                    </div>
                                                                                                ) : (
                                                                                                    <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                                                                                                        <span className="text-2xl mb-2 block">📚</span>
                                                                                                        <p>No training activities found for this team member</p>
                                                                                                    </div>
                                                                                                )}
                                                                                            </div>
                                                                                        </td>
                                                                                    </tr>
                                                                                )}
                                                                            </Fragment>
                                                                        );
                                                                    })}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </Fragment>
                                );
                            }) : (
                                <tr>
                                    <td colSpan="8" className="text-center py-8 text-gray-500 dark:text-gray-400">
                                        {searchFilter.trim() ? "No teams match your search criteria" : "No teams found"}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    const renderMyIDPView = () => {
        // Safety check for records
        if (!records || records.length === 0) {
            return (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 text-center">
                    <div className="text-gray-500 dark:text-gray-400">
                        <span className="text-4xl mb-4 block">📋</span>
                        <h3 className="text-lg font-semibold mb-2">No IDP Data Found</h3>
                        <p>Your Individual Development Plan data is not available yet.</p>
                    </div>
                </div>
            );
        }

        return (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden transition-all border border-gray-200 dark:border-gray-700">
                {/* Header */}
                <div className="bg-yellow-300 dark:bg-yellow-600 text-black dark:text-white p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="bg-white dark:bg-gray-800 p-2 rounded">
                            <div className="w-6 h-6 bg-yellow-300 dark:bg-yellow-600 rounded"></div>
                        </div>
                        <div>
                            <h2 className="text-xl font-bold">Learning and Development</h2>
                            <h3 className="text-lg">Individual Development Plan</h3>
                        </div>
                    </div>
                    <div className="bg-white dark:bg-gray-800 text-gray-400 dark:text-gray-500 p-4 flex justify-center items-center">
                        <div className="relative">
                            {/* Clickable Profile Image */}
                            <label
                                htmlFor="header-profile-upload"
                                className="cursor-pointer w-24 h-24 rounded-full overflow-hidden bg-gray-200 dark:bg-gray-600 block"
                                title="Click to change profile image"
                            >
                                {profileImagePreview ? (
                                    <img
                                        src={profileImagePreview}
                                        alt="Profile preview"
                                        className="w-full h-full object-cover"
                                    />
                                ) : userProfileImage?.image ? (
                                    <img
                                        src={userProfileImage.image}
                                        alt="Profile"
                                        className="w-full h-full object-cover"
                                    />
                                ) : user?.profileImage?.image ? (
                                    <img
                                        src={user.profileImage.image}
                                        alt="Profile"
                                        className="w-full h-full object-cover"
                                    />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center">
                                        <span className="text-4xl text-gray-400 dark:text-gray-500">👤</span>
                                    </div>
                                )}
                            </label>

                            {/* Hidden file input */}
                            <input
                                type="file"
                                accept="image/jpeg,image/jpg,image/png,image/webp"
                                onChange={handleProfileImageSelect}
                                className="hidden"
                                id="header-profile-upload"
                                disabled={uploadingProfileImage}
                            />

                            {/* Upload Button (optional) */}
                            {profileImage && (
                                <div className="absolute -bottom-2 -right-2">
                                    <button
                                        onClick={handleProfileImageUpload}
                                        disabled={uploadingProfileImage}
                                        className="w-8 h-8 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white rounded-full flex items-center justify-center text-xs transition-colors shadow-lg"
                                        title="Upload Image"
                                    >
                                        {uploadingProfileImage ? (
                                            <span className="animate-spin">⏳</span>
                                        ) : (
                                            "📤"
                                        )}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                </div>

                {/* Profile Section */}
                <div className="p-6 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="bg-yellow-300 dark:bg-yellow-600 text-black dark:text-white p-3 rounded text-center">
                            <div className="font-semibold">NAME</div>
                        </div>
                        <div className="bg-gray-100 dark:bg-gray-600 p-3 rounded text-center transition-all">
                            <div className="text-sm text-gray-600 dark:text-gray-300">{user?.name || "____"}</div>
                        </div>
                        <div className="bg-yellow-300 dark:bg-yellow-600 text-black dark:text-white p-3 rounded text-center">
                            <div className="font-semibold">DEPARTMENT</div>
                        </div>
                        <div className="bg-gray-100 dark:bg-gray-600 p-3 rounded text-center">
                            <div className="text-xs text-gray-600 dark:text-gray-300">{records[0]?.department || user?.department || "____"}</div>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-2">
                        <div className="bg-yellow-300 dark:bg-yellow-600 text-black dark:text-white p-3 rounded text-center">
                            <div className="font-semibold">START DATE OF IDP</div>
                        </div>
                        <div className="bg-gray-100 dark:bg-gray-600 p-3 rounded text-center">
                            <div className="text-sm text-gray-600 dark:text-gray-300">
                                {(() => {
                                    const activities = records[0]?.developmentalActivities || [];
                                    if (activities.length === 0) return "____";

                                    const validDates = activities
                                        .map(activity => parseDate(activity.startdate))
                                        .filter(date => date !== null)
                                        .sort((a, b) => a - b);

                                    return validDates.length > 0
                                        ? validDates[0].toLocaleDateString("en-PH", {
                                            year: "numeric",
                                            month: "short",
                                            day: "numeric"
                                        })
                                        : "____";
                                })()}
                            </div>
                        </div>
                        <div className="bg-yellow-300 dark:bg-yellow-600 text-black dark:text-white p-3 rounded text-center">
                            <div className="font-semibold">END DATE OF IDP</div>
                        </div>
                        <div className="bg-gray-100 dark:bg-gray-600 p-3 rounded text-center">
                            <div className="text-sm text-gray-600 dark:text-gray-300">
                                {(() => {
                                    const activities = records[0]?.developmentalActivities || [];
                                    if (activities.length === 0) return "____";

                                    const validDates = activities
                                        .map(activity => parseDate(activity.enddate))
                                        .filter(date => date !== null)
                                        .sort((a, b) => b - a);

                                    return validDates.length > 0
                                        ? validDates[0].toLocaleDateString("en-PH", {
                                            year: "numeric",
                                            month: "short",
                                            day: "numeric"
                                        })
                                        : "____";
                                })()}
                            </div>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-2">
                        <div className="bg-yellow-300 dark:bg-yellow-600 text-black dark:text-white p-3 rounded text-center">
                            <div className="font-semibold">POSITION</div>
                        </div>
                        <div className="bg-gray-100 dark:bg-gray-600 p-3 rounded text-center">
                            <div className="text-sm text-gray-600 dark:text-gray-300">{user?.role || "____"}</div>
                        </div>
                        <div className="bg-yellow-300 dark:bg-yellow-600 text-black dark:text-white p-3 rounded text-center">
                            <div className="font-semibold">IMMEDIATE SUPERVISOR</div>
                        </div>
                        <div className="bg-gray-100 dark:bg-gray-600 p-3 rounded text-center">
                            <div className="text-sm text-gray-600 dark:text-gray-300">{records[0]?.supervisor || user?.supervisor || "____"}</div>
                        </div>
                    </div>
                </div>

                {/* Main Content Area */}
                <div className="p-6">
                    {/* Developmental Activities Section */}
                    <div className="mb-8">
                        <div className="bg-yellow-300 dark:bg-yellow-600 text-black dark:text-white p-3 mb-4">
                            <h3 className="text-lg font-bold flex items-center gap-2">
                                <span className="bg-white dark:bg-gray-800 text-yellow-600 dark:text-yellow-400 w-6 h-6 rounded-full flex items-center justify-center text-sm">📋</span>
                                Developmental Activities
                            </h3>
                            <p className="text-xs mt-2 text-black/70 dark:text-white/70">
                                💡 Tip: Click on percentage values to edit them directly
                            </p>
                        </div>

                        <div className="overflow-x-auto">
                            <table className="w-full border-collapse border border-gray-300 dark:border-gray-600 text-xs">
                                <thead>
                                    <tr className="bg-yellow-300 dark:bg-yellow-600 text-black dark:text-white">
                                        <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">DATE</th>
                                        <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">DEVELOPMENTAL ACTIVITIES</th>
                                        <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">EVENT CATEGORY</th>
                                        <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">LINKS</th>
                                        <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">EXPECTED OUTCOME</th>
                                        <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">STATUS</th>
                                        <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">PRIORITY</th>
                                        <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">AMOUNT</th>
                                        <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">LOCK-IN PERIOD</th>
                                        <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">PERCENTAGE</th>
                                        <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">VALIDITY</th>
                                        <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">START DATE</th>
                                        <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">END DATE</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(records[0]?.developmentalActivities || []).length > 0 ? (
                                        (records[0]?.developmentalActivities || []).map((activity, index) => (
                                            <tr key={index} className={`group ${index % 2 === 0 ? "bg-gray-50 dark:bg-gray-800" : "bg-white dark:bg-gray-900"} hover:bg-yellow-50 dark:hover:bg-yellow-900/50`}>
                                                <td className="border border-gray-300 dark:border-gray-600 p-2 text-gray-700 dark:text-gray-300">{activity.date || "N/A"}</td>
                                                <td className="border border-gray-300 dark:border-gray-600 p-2 text-gray-700 dark:text-gray-300">{activity.activity || "N/A"}</td>
                                                <td className="border border-gray-300 dark:border-gray-600 p-2">
                                                    <span className="px-2 py-1 rounded text-xs font-medium bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200">
                                                        {activity.eventCategory || "N/A"}
                                                    </span>
                                                </td>
                                                <td className="border border-gray-300 dark:border-gray-600 p-2 text-yellow-600 dark:text-yellow-400 underline">
                                                    {activity.links ? (
                                                        <a href={activity.links} target="_blank" rel="noopener noreferrer">
                                                            View
                                                        </a>
                                                    ) : (
                                                        "N/A"
                                                    )}
                                                </td>
                                                <td className="border border-gray-300 dark:border-gray-600 p-2 text-gray-700 dark:text-gray-300">{activity.expectedOutcome || "N/A"}</td>
                                                <td className="border border-gray-300 dark:border-gray-600 p-2">
                                                    <span className={`px-2 py-1 rounded text-xs font-medium ${activity.status === 'Attended' ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' :
                                                        activity.status === 'Scheduled' ? 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200' :
                                                            activity.status === 'Cancelled' ? 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200' :
                                                                'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                                                        }`}>
                                                        {activity.status || "N/A"}
                                                    </span>
                                                </td>
                                                <td className="border border-gray-300 dark:border-gray-600 p-2">
                                                    <span className={`px-2 py-1 rounded text-xs font-medium ${activity.priority === 'High' ? 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200' :
                                                        activity.priority === 'Medium' ? 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200' :
                                                            activity.priority === 'Low' ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' :
                                                                'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                                                        }`}>
                                                        {activity.priority || "N/A"}
                                                    </span>
                                                </td>
                                                <td className="border border-gray-300 dark:border-gray-600 p-2 text-gray-700 dark:text-gray-300">
                                                    {activity.amount ? `₱${Number(activity.amount).toLocaleString()}` : "N/A"}
                                                </td>
                                                <td className="border border-gray-300 dark:border-gray-600 p-2 text-gray-700 dark:text-gray-300">{activity.lockin || "N/A"}</td>
                                                <td className="border border-gray-300 dark:border-gray-600 p-2">
                                                    {editingPercentage?.activityIndex === index ? (
                                                        // Editing mode
                                                        <div className="flex items-center gap-2">
                                                            <div className="flex items-center gap-1">
                                                                <input
                                                                    type="text"
                                                                    value={editingPercentage.value}
                                                                    onChange={(e) => handlePercentageChange(e.target.value)}
                                                                    onKeyDown={handlePercentageKeyPress}
                                                                    className="w-12 px-1 py-1 text-xs border border-yellow-300 dark:border-yellow-600 rounded focus:outline-none focus:border-yellow-500 focus:ring-1 focus:ring-yellow-200 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                                                    maxLength="3"
                                                                    placeholder="0-100"
                                                                    autoFocus
                                                                    onFocus={(e) => e.target.select()}
                                                                />
                                                                <span className="text-xs text-gray-700 dark:text-gray-300">%</span>
                                                            </div>
                                                            <div className="flex gap-1">
                                                                <button
                                                                    onClick={handlePercentageSave}
                                                                    disabled={savingPercentage}
                                                                    className="px-1 py-0.5 bg-green-500 dark:bg-green-600 text-white text-xs rounded hover:bg-green-600 dark:hover:bg-green-700 disabled:opacity-50"
                                                                    title="Save"
                                                                >
                                                                    {savingPercentage ? "..." : "✓"}
                                                                </button>
                                                                <button
                                                                    onClick={handlePercentageCancel}
                                                                    disabled={savingPercentage}
                                                                    className="px-1 py-0.5 bg-gray-500 dark:bg-gray-600 text-white text-xs rounded hover:bg-gray-600 dark:hover:bg-gray-700 disabled:opacity-50"
                                                                    title="Cancel"
                                                                >
                                                                    ✕
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        // Display mode
                                                        <div>
                                                            <div
                                                                className="flex items-center gap-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 p-1 rounded group"
                                                                onClick={() => handlePercentageEdit(index, activity.percentage || "0%")}
                                                                title="Click to edit percentage"
                                                            >
                                                                <div className="flex-1 bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                                                                    <div
                                                                        className="bg-yellow-600 dark:bg-yellow-500 h-2 rounded-full transition-all duration-300"
                                                                        style={{ width: `${activity.percentage?.replace('%', '') || 0}%` }}
                                                                    ></div>
                                                                </div>
                                                                <span className="text-xs font-medium text-gray-700 dark:text-gray-300">{activity.percentage || "0%"}</span>
                                                                <span className="text-xs text-yellow-500 dark:text-yellow-400 opacity-0 group-hover:opacity-100 transition-opacity">✎</span>
                                                            </div>
                                                            {renderCompletionProofViewer(activity)}
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="border border-gray-300 dark:border-gray-600 p-2 text-gray-700 dark:text-gray-300">{activity.validity || "N/A"}</td>
                                                <td className="border border-gray-300 dark:border-gray-600 p-2">
                                                    <span className="text-xs text-gray-600 dark:text-gray-400">
                                                        {(() => {
                                                            const parsedDate = parseDate(activity.startdate);
                                                            return parsedDate
                                                                ? parsedDate.toLocaleDateString("en-PH", {
                                                                    year: "numeric",
                                                                    month: "short",
                                                                    day: "numeric"
                                                                })
                                                                : "N/A";
                                                        })()}
                                                    </span>
                                                </td>
                                                <td className="border border-gray-300 dark:border-gray-600 p-2">
                                                    <span className="text-xs text-gray-600 dark:text-gray-400">
                                                        {(() => {
                                                            const parsedDate = parseDate(activity.enddate);
                                                            return parsedDate
                                                                ? parsedDate.toLocaleDateString("en-PH", {
                                                                    year: "numeric",
                                                                    month: "short",
                                                                    day: "numeric"
                                                                })
                                                                : "N/A";
                                                        })()}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr className="bg-gray-50 dark:bg-gray-800">
                                            <td className="border border-gray-300 dark:border-gray-600 p-2 text-center text-gray-500 dark:text-gray-400" colSpan="13">
                                                No developmental activities found
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Training Types Section */}
                    <div className="mb-8">
                        <div className="bg-yellow-300 dark:bg-yellow-600 text-black dark:text-white p-3 mb-4">
                            <h3 className="text-lg font-bold flex items-center gap-2">
                                <span className="bg-white dark:bg-gray-800 text-yellow-600 dark:text-yellow-400 w-6 h-6 rounded-full flex items-center justify-center text-sm">🏷️</span>
                                Training Types
                            </h3>
                        </div>

                        <div className="overflow-x-auto">
                            <table className="w-full border-collapse border border-gray-300 dark:border-gray-600 text-xs">
                                <thead>
                                    <tr className="bg-yellow-300 dark:bg-yellow-600 text-black dark:text-white">
                                        <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">TYPE</th>
                                        <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">DEVELOPMENTAL ACTIVITIES</th>
                                        <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">COUNT</th>
                                        <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">AVERAGE PROGRESS</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(() => {
                                        const activities = records[0]?.developmentalActivities || [];

                                        if (activities.length === 0) {
                                            return (
                                                <tr className="bg-gray-50 dark:bg-gray-800">
                                                    <td className="border border-gray-300 dark:border-gray-600 p-2 text-center text-gray-500 dark:text-gray-400" colSpan="4">
                                                        No training types found
                                                    </td>
                                                </tr>
                                            );
                                        }

                                        // Group activities by type
                                        const groupedByType = activities.reduce((acc, activity) => {
                                            const type = activity.type || 'Unspecified';
                                            if (!acc[type]) {
                                                acc[type] = [];
                                            }
                                            acc[type].push(activity);
                                            return acc;
                                        }, {});

                                        return Object.entries(groupedByType).map(([type, typeActivities], index) => {
                                            const avgProgress = typeActivities.length > 0
                                                ? Math.round(
                                                    typeActivities.reduce((sum, a) => sum + parseInt(a.percentage?.replace('%', '') || '0'), 0) / typeActivities.length
                                                )
                                                : 0;

                                            return (
                                                <tr key={index} className={`${index % 2 === 0 ? "bg-gray-50 dark:bg-gray-800" : "bg-white dark:bg-gray-900"} hover:bg-yellow-50 dark:hover:bg-yellow-900/50`}>
                                                    <td className="border border-gray-300 dark:border-gray-600 p-2">
                                                        <span className={`px-3 py-1 rounded text-xs font-medium ${type === 'External'
                                                            ? 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200'
                                                            : type === 'Internal'
                                                                ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
                                                                : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                                                            }`}>
                                                            {type}
                                                        </span>
                                                    </td>
                                                    <td className="border border-gray-300 dark:border-gray-600 p-2">
                                                        <div className="space-y-1">
                                                            {typeActivities.map((activity, actIndex) => (
                                                                <div key={actIndex} className="text-xs text-gray-700 dark:text-gray-300">
                                                                    • {activity.activity || 'Untitled Activity'}
                                                                    <span className="ml-2 text-gray-500 dark:text-gray-400">
                                                                        ({activity.percentage || '0%'})
                                                                    </span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </td>
                                                    <td className="border border-gray-300 dark:border-gray-600 p-2 text-center">
                                                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200">
                                                            {typeActivities.length}
                                                        </span>
                                                    </td>
                                                    <td className="border border-gray-300 dark:border-gray-600 p-2">
                                                        <div className="flex items-center gap-2">
                                                            <div className="flex-1 bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                                                                <div
                                                                    className="bg-yellow-600 dark:bg-yellow-500 h-2 rounded-full transition-all duration-300"
                                                                    style={{ width: `${avgProgress}%` }}
                                                                ></div>
                                                            </div>
                                                            <span className="text-xs font-medium text-gray-700 dark:text-gray-300 min-w-[30px]">
                                                                {avgProgress}%
                                                            </span>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        });
                                    })()}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Bottom Section with Overall Progress and Event Categories */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                        {/* Overall Progress */}
                        <div>
                            <div className="bg-yellow-300 dark:bg-yellow-600 text-black dark:text-white p-3 mb-4">
                                <h3 className="text-lg font-bold flex items-center gap-2">
                                    <span className="bg-white dark:bg-gray-800 text-yellow-600 dark:text-yellow-400 w-6 h-6 rounded-full flex items-center justify-center text-sm">📊</span>
                                    Overall Progress
                                </h3>
                            </div>
                            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                                {(() => {
                                    const activities = (records && records[0] && records[0].developmentalActivities) ? records[0].developmentalActivities : [];
                                    if (activities.length === 0) {
                                        return (
                                            <div className="text-center py-8">
                                                <div className="text-6xl font-bold text-gray-400 dark:text-gray-500 mb-2">0%</div>
                                                <p className="text-gray-500 dark:text-gray-400">No activities available</p>
                                            </div>
                                        );
                                    }

                                    // Calculate overall percentage
                                    const totalPercentage = activities.reduce((sum, activity) => {
                                        return sum + parseInt(activity.percentage?.replace('%', '') || '0');
                                    }, 0);

                                    const overallPercentage = Math.round(totalPercentage / activities.length);
                                    const progressColor = getProgressColor(overallPercentage);
                                    const progressMessage = getProgressMessage(overallPercentage);

                                    return (
                                        <div className="text-center">
                                            {/* Large Percentage Display */}
                                            <div className={`text-8xl font-bold mb-4 ${progressColor}`}>
                                                {overallPercentage}%
                                            </div>

                                            {/* Progress Message */}
                                            <div className="mb-6">
                                                <p className={`text-xl font-semibold ${progressColor} mb-2`}>
                                                    {progressMessage}
                                                </p>
                                                <p className="text-gray-600 dark:text-gray-400">
                                                    Based on {activities.length} developmental activit{activities.length === 1 ? 'y' : 'ies'}
                                                </p>
                                            </div>

                                            {/* Progress Bar */}
                                            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-4 mb-4">
                                                <div
                                                    className="bg-gradient-to-r from-yellow-500 to-green-500 h-4 rounded-full transition-all duration-500 ease-in-out"
                                                    style={{ width: `${overallPercentage}%` }}
                                                ></div>
                                            </div>

                                            {/* Additional Stats */}
                                            <div className="grid grid-cols-2 gap-4 mt-6 pt-4 border-t border-gray-200 dark:border-gray-600">
                                                <div>
                                                    <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                                                        {activities.filter(a => parseInt(a.percentage?.replace('%', '') || '0') === 100).length}
                                                    </div>
                                                    <div className="text-sm text-gray-500 dark:text-gray-400">Completed</div>
                                                </div>
                                                <div>
                                                    <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                                                        {activities.filter(a => parseInt(a.percentage?.replace('%', '') || '0') > 0 && parseInt(a.percentage?.replace('%', '') || '0') < 100).length}
                                                    </div>
                                                    <div className="text-sm text-gray-500 dark:text-gray-400">In Progress</div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })()}
                            </div>
                        </div>

                        {/* Event Categories */}
                        <div>
                            <div className="bg-yellow-300 dark:bg-yellow-600 text-black dark:text-white p-3 mb-4">
                                <h3 className="text-lg font-bold flex items-center gap-2">
                                    <span className="bg-white dark:bg-gray-800 text-yellow-600 dark:text-yellow-400 w-6 h-6 rounded-full flex items-center justify-center text-sm">🗂️</span>
                                    Event categories
                                </h3>
                            </div>

                            <div className="space-y-4">
                                {(() => {
                                    const activities = records[0]?.developmentalActivities || [];

                                    if (activities.length === 0) {
                                        return (
                                            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded border border-gray-200 dark:border-gray-600">
                                                <h4 className="font-semibold text-yellow-700 dark:text-yellow-300 mb-2">No categories available</h4>
                                                <div className="text-sm text-gray-700 dark:text-gray-300">• Please add event categories</div>
                                            </div>
                                        );
                                    }

                                    const groupedByCategory = activities.reduce((acc, activity) => {
                                        const category = activity.eventCategory || 'Uncategorized';
                                        if (!acc[category]) acc[category] = [];
                                        acc[category].push(activity.activity || 'Untitled Activity');
                                        return acc;
                                    }, {});

                                    return Object.entries(groupedByCategory).map(([category, activityList], index) => {
                                        const sectionId = `category-section-${index}`;
                                        const iconId = `icon-${index}`;
                                        return (
                                            <div key={index} className="bg-gray-50 dark:bg-gray-700 p-4 rounded border border-gray-200 dark:border-gray-600">
                                                <div className="flex justify-between items-center mb-2">
                                                    <h4 className="font-semibold text-yellow-700 dark:text-yellow-300">{category}</h4>
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            const content = document.getElementById(sectionId);
                                                            const icon = document.getElementById(iconId);
                                                            if (content) content.classList.toggle("hidden");
                                                            if (icon) icon.classList.toggle("rotate-180");
                                                        }}
                                                        className="transition-transform duration-200"
                                                    >
                                                        <svg
                                                            id={iconId}
                                                            className="w-5 h-5 text-gray-600 dark:text-gray-300 transform transition-transform duration-200"
                                                            fill="none"
                                                            stroke="currentColor"
                                                            viewBox="0 0 24 24"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                        >
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                                                        </svg>
                                                    </button>
                                                </div>

                                                <div id={sectionId} className="space-y-1 hidden">
                                                    {activityList.map((activityName, itemIndex) => (
                                                        <div key={itemIndex} className="text-sm text-gray-700 dark:text-gray-300">• {activityName}</div>
                                                    ))}
                                                </div>
                                            </div>
                                        );
                                    });
                                })()}
                            </div>
                        </div>

                    </div>

                    {/* Remarks Section */}
                    <div>
                        <h3 className="text-lg font-semibold mb-4 text-yellow-700 dark:text-yellow-300">Remarks Table</h3>
                        <table className="w-full border-collapse border border-gray-300 dark:border-gray-600 text-sm">
                            <thead>
                                <tr className="bg-yellow-300 dark:bg-yellow-600 text-black dark:text-white">
                                    <th className="border border-gray-300 dark:border-gray-600 p-3 text-left">DATE</th>
                                    <th className="border border-gray-300 dark:border-gray-600 p-3 text-left">SUPERVISOR REMARKS</th>
                                    <th className="border border-gray-300 dark:border-gray-600 p-3 text-left">L&D REMARKS</th>
                                    <th className="border border-gray-300 dark:border-gray-600 p-3 text-left">HR REMARKS</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(records[0]?.remarks || []).length > 0 ? (
                                    (records[0]?.remarks || []).map((remark, index) => (
                                        <tr key={index} className={index % 2 === 0 ? "bg-gray-50 dark:bg-gray-800" : "bg-white dark:bg-gray-900"}>
                                            <td className="border border-gray-300 dark:border-gray-600 p-4 text-gray-700 dark:text-gray-300">{remark.date}</td>
                                            <td className="border border-gray-300 dark:border-gray-600 p-4 text-gray-700 dark:text-gray-300">{remark.supervisorRemarks}</td>
                                            <td className="border border-gray-300 dark:border-gray-600 p-4 text-gray-700 dark:text-gray-300">{remark.ldRemarks}</td>
                                            <td className="border border-gray-300 dark:border-gray-600 p-4 text-gray-700 dark:text-gray-300">{remark.hrRemarks}</td>
                                        </tr>
                                    ))
                                ) : (
                                    <>
                                        <tr className="bg-gray-50 dark:bg-gray-800">
                                            <td className="border border-gray-300 dark:border-gray-600 p-4 text-gray-500 dark:text-gray-400">No remarks yet</td>
                                            <td className="border border-gray-300 dark:border-gray-600 p-4 text-gray-500 dark:text-gray-400">________________________</td>
                                            <td className="border border-gray-300 dark:border-gray-600 p-4 text-gray-500 dark:text-gray-400">________________________</td>
                                            <td className="border border-gray-300 dark:border-gray-600 p-4 text-gray-500 dark:text-gray-400">________________________</td>
                                        </tr>
                                        <tr className="bg-white dark:bg-gray-900">
                                            <td className="border border-gray-300 dark:border-gray-600 p-4 text-gray-500 dark:text-gray-400">__________</td>
                                            <td className="border border-gray-300 dark:border-gray-600 p-4 text-gray-500 dark:text-gray-400">________________________</td>
                                            <td className="border border-gray-300 dark:border-gray-600 p-4 text-gray-500 dark:text-gray-400">________________________</td>
                                            <td className="border border-gray-300 dark:border-gray-600 p-4 text-gray-500 dark:text-gray-400">________________________</td>
                                        </tr>
                                    </>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    };

    // =============================================================================
    // MAIN COMPONENT RENDER
    // =============================================================================

    if (!user) return <p className="p-4 text-gray-800 dark:text-gray-200">Unauthorized - Please log in</p>;

    return (
        <div className="p-6 max-w-full overflow-x-auto bg-gray-50 dark:bg-gray-900 text-gray-800 dark:text-gray-100 min-h-screen transition-all duration-500">
            {/* Header Section */}
            <div className="mb-6">
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Welcome, {user.name} 👋</h1>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                    {canSwitchViews ? "Select a view to get started." : "View your Individual Development Plan below."}
                </p>

                {/* Only show view switcher for authorized roles */}
                {canSwitchViews && (
                    <div className="mt-4 flex gap-2">
                        <button
                            onClick={() => handleViewModeChange("self")}
                            className={`px-4 py-2 rounded transition-colors ${viewMode === "self"
                                ? "bg-yellow-600 text-white dark:bg-yellow-500"
                                : "bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600"
                                }`}
                        >
                            My IDP
                        </button>
                        <button
                            onClick={() => handleViewModeChange("monitoring")}
                            className={`px-4 py-2 rounded transition-colors ${viewMode === "monitoring"
                                ? "bg-yellow-600 text-white dark:bg-yellow-500"
                                : "bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600"
                                }`}
                        >
                            IDP Monitoring
                        </button>
                    </div>
                )}

                {/* Show current role for debugging */}
                {/* Show current role and IDP access level */}
                <div className="mt-2 text-xs text-gray-500 dark:text-gray-400 flex items-center gap-4 flex-wrap">
                    <span>Role: {user.role || 'No role assigned'}</span>
                    <span>View: {viewMode}</span>

                    {/* Show IDP access level */}
                    {hasOrganizationAccess(user?.role) ? (
                        <span className="px-2 py-1 rounded text-xs bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200">
                            🏢 Organization IDP Access
                        </span>
                    ) : canHaveTeamAccess(user?.role) ? (
                        <span className={`px-2 py-1 rounded text-xs ${hasTeamAccess
                            ? "bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200"
                            : "bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200"
                            }`}>
                            👥 {hasTeamAccess ? "Team IDP Access" : "No Team Assigned"}
                        </span>
                    ) : (
                        <span className="px-2 py-1 rounded text-xs bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                            👤 Personal IDP Only
                        </span>
                    )}
                </div>
            </div>

            {/* Main Content */}
            {loading ? (
                <div className="flex items-center justify-center h-64">
                    <div className="text-lg text-gray-800 dark:text-gray-200">Loading your IDP data...</div>
                </div>
            ) : (
                <>
                    {viewMode === "monitoring" && canSwitchViews ? (
                        <div className="w-full">
                            {/* Add Training Button - Only for managers, HR, EXCOM */}
                            {/* Action Buttons - Add Training and Download Report */}
                            {(hasOrganizationAccess(user.role) || hasTeamAccess) && (<div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                                <div className="flex items-center gap-3">
                                    <button
                                        onClick={handleOpenAddTrainingModal}
                                        className="px-6 py-3 bg-yellow-600 dark:bg-yellow-500 text-white rounded-lg hover:bg-yellow-700 dark:hover:bg-yellow-600 transition-colors flex items-center gap-2"
                                    >
                                        <span className="text-lg">📚</span>
                                        Add Training
                                    </button>

                                    {/* Download Report Button - Only for HR and EXCOM */}
                                    {/* Download Report Button - For managers, HR and EXCOM */}
                                    {["manager", "hr", "excom", "supervisor"].includes(user.role) && (
                                        <button
                                            onClick={handleDownloadReport}
                                            disabled={downloadingReport}
                                            className="px-6 py-3 bg-yellow-600 dark:bg-yellow-500 text-white rounded-lg hover:bg-gray-700 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                                        >
                                            {downloadingReport ? (
                                                <>
                                                    <span className="animate-spin">⏳</span>
                                                    Generating...
                                                </>
                                            ) : (
                                                <>
                                                    <span className="text-lg">📊</span>
                                                    Download Report
                                                </>
                                            )}
                                        </button>
                                    )}
                                </div>

                                <div className="flex items-center gap-2 text-xs">
                                    <span className="px-3 py-1 bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 rounded-full">
                                        📋 Total Records: {(records || []).length}
                                    </span>
                                </div>
                            </div>
                            )}

                            {/* Monitoring View Components */}
                            {renderProgressSummary()}

                            {/* IDP Summary Header */}
                            <div className="bg-yellow-100 dark:bg-yellow-900/50 p-4 mb-6 rounded-lg border border-yellow-200 dark:border-yellow-800">
                                <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 bg-yellow-500 dark:bg-yellow-600 rounded-full flex items-center justify-center">
                                        <span className="text-white font-bold">🔍</span>
                                    </div>
                                    <h2 className="text-2xl font-bold text-yellow-700 dark:text-yellow-300">IDP SUMMARY</h2>
                                </div>
                            </div>

                            {/* Team Accordion Table */}
                            {renderTeamAccordionTable()}
                        </div>
                    ) : (
                        /* My IDP View - Default for agents and available for all users */
                        renderMyIDPView()
                    )}
                </>
            )}

            {/* Add Training Modal */}
            {showAddTrainingModal && renderAddTrainingModal()}
            {showCompletionModal && renderCompletionModal()}
            {showProofModal && renderProofModal()}




        </div>
    );
};

export default IDPMonitoringDashboard;
